import { Hls, HlsVideoElement } from 'hls-video-element';
import { CastableMediaMixin } from 'castable-video/castable-mixin.js';
import {
  type CodecCapabilityTable,
  type CodecLevel,
  probeCodecCapabilities,
  selectCodecFamily,
} from '../utils/codec-filter.ts';
import {
  getPlayerSizeCapIndex,
  type IndexedStartLevelInput,
  pickOriginalStartLevelForPlayerSize,
} from '../utils/start-level.ts';
import { StreamDrmEngine } from '../utils/stream-drm-engine.js';
import {
  isChrome,
  isFairPlayBrowser,
  isGecko,
  isIOS,
  isMobileDevice,
  isSafari,
  isWebKitEngine,
} from '../utils/browser-detection.js';
import {
  buildActiveTrackIds,
  pickReceiverAudioTrack,
} from '../utils/cast-audio-tracks.ts';
import {
  getCastMediaSession,
  getEditTracksInfoRequest,
  isCastSessionResumed,
  onCastMediaInfoChanged,
  setCastPlaybackRate,
} from '../utils/cast-sender.ts';
import {
  buildCmcdBeaconUrl,
  buildWaitingCmcdLine,
  isWaitingForLiveStreamStart,
  shouldSendWaitingCmcdBeacon,
} from '../utils/cmcd-beacon.ts';
import {
  classifyFatalNetworkError,
  type FatalNetworkErrorKind,
  getHlsErrorStatusCode,
  getHlsErrorUrl,
  isDrmError,
  isManifestLoadError,
  isPlaylistForbiddenError,
  isPlaylistNotFoundError,
} from '../utils/hls-errors.ts';
import {
  classifyPlaylistDelivery,
  type PlaylistDelivery,
} from '../utils/playlist-delivery.ts';
import { probeLivePlaylist } from '../utils/live-playlist-probe.ts';
import {
  isConfirmedOfflineLiveState,
  POSTER_ARTWORK_STATE_EVENT,
  resolveThumbnailPosterUpdate,
  shouldParkOnThumbnail,
  shouldShowStatusOverlay,
} from '../utils/live-state.ts';
import {
  isSameSignedSource,
  isSignedSourceExpiring,
} from '../utils/signed-source.ts';
import { resolveLiveStatusPollDelay } from '../utils/live-status-poll.ts';
import { getPlaybackSessionId } from '../utils/playback-session.ts';
import { isDebugEnabled } from '../utils/player-prefs.ts';
import {
  applyWidevineCodecPolicyToConfigurations,
  getWidevinePlaybackLevelIndicesToRemove,
  getWidevineVideoContentType,
  isWidevineCodecSupported,
  probeWidevineCodecSupport,
  resolveMaxSoftwareWidevineResolution,
  resolveWidevineCodecPolicy,
  resolveWidevineRobustness,
  type WidevineRobustness,
} from '../utils/widevine-policy.ts';

type MediaControllerElement = HTMLElement & {
  dataset: DOMStringMap;
};

type MediaSourceLike = {
  isTypeSupported(type: string): boolean;
};

type CodecFilterCandidate = CodecLevel;

type FilteredLevel = CodecFilterCandidate & {
  /** Index in hls.js's current level list, for start level selection. */
  originalIndex: number;
  /**
   * Rendition id assigned by hls-video-element at MANIFEST_PARSED (the
   * manifest index). hls.removeLevel() does not renumber renditions, so the
   * quality menu must select by this id, not by the current hls.js index.
   */
  renditionId: string;
};

type LiveStateOptions = {
  isPlayable?: boolean;
  isLiveActive?: boolean;
  showCountdown?: boolean;
  scheduledStartTime?: string | null;
  showStatus?: boolean;
  statusMessage?: string | null;
  thumbnailUrl?: string | null;
  status?: number | null;
  allowAutoResume?: boolean;
};

type LiveStatusResponse = {
  videoPlaylistUrl?: string;
  status?: number;
  isPlayable?: boolean;
  isLiveActive?: boolean;
  showCountdown?: boolean;
  scheduledStartTime?: string | null;
  showStatus?: boolean;
  statusMessage?: string | null;
  thumbnailUrl?: string | null;
};

type RenditionMenuElement = HTMLElement & {
  filteredLevels?: FilteredLevel[];
};

type BunnyHlsConfigOptions = {
  shouldPreload: boolean;
  edrmEnabled: boolean;
  useInternalFairPlay: boolean;
  levelCap: boolean;
  isLiveManagedSource: boolean;
};

export function createBunnyHlsConfig({
  shouldPreload,
  edrmEnabled,
  useInternalFairPlay,
  levelCap,
  isLiveManagedSource,
}: BunnyHlsConfigOptions): Record<string, unknown> {
  const config: Record<string, unknown> = {
    debug: false,
    emeEnabled: edrmEnabled && !useInternalFairPlay,
    enableWorker: true,
    autoStartLoad: shouldPreload,
    maxMaxBufferLength: 120,
    maxBufferLength: 120,
    abrEwmaDefaultEstimate: 5000000,
    // Feed actual measured fragment download rates back into the EWMA
    // estimator so bandwidth converges to reality faster.
    abrMaxWithRealBitrate: true,
    startLevel: -1,
  };

  if (levelCap) {
    config.capLevelToPlayerSize = true;
    config.ignoreDevicePixelRatio = true;
  }

  if (isLiveManagedSource) {
    const playlistLoadPolicy = {
      default: {
        maxTimeToFirstByteMs: 10000,
        maxLoadTimeMs: 20000,
        timeoutRetry: {
          maxNumRetry: 4,
          retryDelayMs: 0,
          maxRetryDelayMs: 0,
        },
        errorRetry: {
          maxNumRetry: 4,
          retryDelayMs: 1000,
          maxRetryDelayMs: 8000,
        },
      },
    };

    Object.assign(config, {
      playlistLoadPolicy,
      appendErrorMaxRetry: 5,
      maxBufferHole: 4,
      nudgeMaxRetry: 10,
      nudgeOffset: 1,
      liveDurationInfinity: true,
      liveSyncDurationCount: 3,
      liveMaxLatencyDurationCount: 6,
      maxMaxBufferLength: 30,
      maxBufferLength: 30,
      backBufferLength: 30,
    });
  }

  return config;
}

/**
 * Minimum gap between waiting-viewer CMCD beacons, independent of the (much
 * more frequent) live-status poll interval - this metric doesn't need
 * per-poll granularity, and hls.js's own CMCD event mode defaults to the
 * same 30s interval.
 */
const WAITING_CMCD_BEACON_INTERVAL_MS = 30_000;

interface LocalAudioTrackLike {
  enabled?: boolean;
  language?: string;
  label?: string;
}

interface LocalAudioTrackListLike extends EventTarget {
  readonly length: number;
  readonly [index: number]: LocalAudioTrackLike | undefined;
}

class BunnyStreamVideoElement extends CastableMediaMixin(HlsVideoElement) {
  declare api: Hls | null;
  declare config: Record<string, unknown>;
  declare nativeEl: HTMLVideoElement;
  declare src: string;
  declare isJit: boolean;
  declare mediaSourceSupported: boolean;

  static override get observedAttributes(): string[] {
    return (super.observedAttributes || []).concat([
      'content-src',
      'disable-ios-player',
      'remember-position',
      'drm-robustness',
      'drm-video-robustness',
      'drm-version',
      'video-id',
      'library-id',
      'drm-engine',
      'drm-provider',
      'is-jit',
      'base-url',
      'level-cap',
      'widevine-sd-only-for-l3',
      'max-software-widevine-resolution',
      'start-time',
      'chromecast',
      'airplay',
      'live-status-url',
      'live-status-interval',
      'live-preview',
    ]);
  }

  _waitingKeys = false;
  _edrmEnabled = false;
  _videoId: string | null = this.getAttribute('video-id');
  _libraryId: string | null = this.getAttribute('library-id');
  _drmEngine: string | null = this.getAttribute('drm-engine');
  _drmProvider: string | null = this.getAttribute('drm-provider');
  _drmVersion: number | null = this.#parseDrmVersion(
    this.getAttribute('drm-version'),
  );
  _baseUrl = this.getAttribute('base-url') || 'https://video.bunnycdn.com';
  _levelCap = this.getAttribute('level-cap') === 'true';
  _widevineSdOnlyForL3 =
    this.getAttribute('widevine-sd-only-for-l3') === 'true';
  _maxSoftwareWidevineResolution = resolveMaxSoftwareWidevineResolution(
    this.getAttribute('max-software-widevine-resolution'),
    this._widevineSdOnlyForL3,
  );
  _selectedWidevineRobustness: WidevineRobustness | null = null;
  _restrictWidevineToSd = false;
  _startTime = parseFloat(this.getAttribute('start-time') || '0');
  _fragmentsLoaded = 0;
  _filteredLevels: FilteredLevel[] | null = null;
  // Level object -> rendition id given by hls-video-element for this manifest.
  #manifestRenditionIds = new WeakMap<object, number>();
  // One Media Capabilities verdict per page, shared by every player on it.
  // The probe starts when the bundle evaluates, before any element connects
  // or fetches a manifest. null until it resolves; a manifest parsed earlier
  // (decodingInfo() slower than the manifest fetch) falls back to MediaSource
  // support alone. load() must not wait for it: the ad controller relies on
  // the source being applied one microtask after load() is called.
  static #codecCapabilities: CodecCapabilityTable | null = null;
  static #codecCapabilitiesProbe: Promise<void> | null = null;

  static warmCodecCapabilities(): void {
    if (this.#codecCapabilitiesProbe) return;
    this.#codecCapabilitiesProbe = probeCodecCapabilities(
      globalThis.navigator?.mediaCapabilities,
      isMobileDevice(),
    ).then((table) => {
      this.#codecCapabilities = table;
    });
  }
  _internalDrmInitialized = false; // Guard against double initialization
  _streamDrmEngine: StreamDrmEngine | null = null; // FairPlay DRM engine instance for internal DRM mode
  _liveStatusUrl: string | null = this.getAttribute('live-status-url');
  _liveStatusInterval: number = this.#parseLiveStatusInterval(
    this.getAttribute('live-status-interval'),
  );
  _isPlayable = false;
  _hasSeenLiveActive = false;
  _liveStatusTimer: ReturnType<typeof setTimeout> | null = null;
  _liveStatusInFlight = false;
  /** Delay the last poll asked for; null until the first response lands. */
  _nextPollDelayMs: number | null = null;
  /** Consecutive polls made with a playlist retry outstanding. */
  _outstandingRetryPolls = 0;
  /**
   * True between a 404 on the live playlist and the next parsed manifest.
   * Tracked separately from `_needsSourceReload`, which is cleared eagerly as
   * soon as a reload is dispatched - this has to stay true until a manifest
   * actually parses, or the cadence would slow down on the very poll that
   * kicked off a retry and then miss the retry failing.
   */
  _awaitingPlaylist = false;
  // Parked on the thumbnail or last frame: see #syncLiveStoppedState.
  _isLiveStopped = false;
  // A Chromecast session is connected; see #syncCastingOverlay.
  _isCasting = false;
  // The parked poster's image, watched so only a loaded thumbnail counts.
  #stoppedPosterImage: HTMLImageElement | null = null;
  /**
   * The playlist that actually 404'd. Not always the source URL - a level or
   * audio-track playlist can fail while the multivariant manifest is fine.
   */
  _awaitingPlaylistUrl: string | null = null;
  _playlistProbeInFlight = false;
  _livePreview = this.getAttribute('live-preview') === 'true';
  _visibilityHandler: EventListener | null = null;
  _needsSourceReload = false;
  _liveShowStatus = false;
  _liveStatusMessage = '';
  _posterArtworkFailed = false;
  _posterArtworkHandler: EventListener | null = null;
  _posterArtworkListenerAttached = false;
  #lastWaitingCmcdBeaconAt: number | null = null;
  _contentSrc: string | null = this.#normalizeContentSrc(
    this.getAttribute('content-src'),
  );
  _rememberPositionEnabled: boolean = this.#parseRememberPositionAttribute(
    this.getAttribute('remember-position'),
  );
  _rememberPositionKey: string | null = this.#getRememberPositionStorageKey();
  _pendingRememberedPosition: number | null = null;
  _rememberPositionListenersAttached = false;
  _mediaErrorRecoveryAttempts = 0;
  // Source for which the one reload allowed after a hidden manifest failure
  // has already been spent; see #resolveHiddenManifestFailure.
  _manifestReloadAttemptedFor: string | null = null;
  // A newer signing was attached after a playlist 403 and no manifest has
  // parsed since; a second 403 is then a real refusal, not a lapsed token.
  _forbiddenReattachPending = false;
  #nativeMediaListenersAttached = false;
  /** An overlay from #dispatchPlayerError is showing and a later success should clear it. */
  #playerErrorShown = false;

  /**
   * Parse DRM version attribute value to integer
   * @param {string|null} value - Attribute value to parse
   * @returns {number|null} Parsed integer or null if value is invalid/absent
   */
  #parseDrmVersion(value: string | null): number | null {
    return value ? parseInt(value, 10) : null;
  }

  #parseLiveStatusInterval(value: string | null): number {
    const parsed = value ? parseInt(value, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 5000;
  }

  #getMediaController(): MediaControllerElement | null {
    if (typeof this.closest !== 'function') return null;
    const controller = this.closest('media-controller');
    return controller instanceof HTMLElement
      ? controller as MediaControllerElement
      : null;
  }

  #isLiveStreamController(): boolean {
    const controller = this.#getMediaController();
    return controller?.dataset?.liveStream === 'true';
  }

  #isLiveManagedSource(): boolean {
    return this.#isLiveStreamController() ||
      this.hasAttribute('live-status-url');
  }

  #shouldAutoSyncContentSrc(): boolean {
    return this.#isLiveManagedSource() ||
      (!this.hasAttribute('ad-tag-src') && !this.hasAttribute('vast-src'));
  }

  #normalizeContentSrc(value: string | null | undefined): string | null {
    if (typeof value !== 'string') return null;
    const normalizedContentSrc = value.trim();
    return normalizedContentSrc.length > 0 ? normalizedContentSrc : null;
  }

  #setControllerData(name: string, value: string | null | undefined): void {
    const controller = this.#getMediaController();
    if (!controller) return;
    if (value === null || value === undefined) {
      controller.removeAttribute(`data-${name}`);
      delete controller.dataset[name];
    } else {
      controller.setAttribute(`data-${name}`, String(value));
    }
  }

  #hasTrailerPosterSource(): boolean {
    const controller = this.#getMediaController();
    if (!controller) return false;
    const trailerPoster = controller.querySelector(
      'bunny-poster-video[slot="poster"]',
    );
    if (!(trailerPoster instanceof HTMLElement)) return false;
    const trailerSrc = trailerPoster.getAttribute('src');
    return typeof trailerSrc === 'string' && trailerSrc.trim().length > 0;
  }

  #syncTrailerActiveState(isLiveActive: boolean): void {
    if (isLiveActive) {
      this._hasSeenLiveActive = true;
    }

    const shouldShowTrailer = this.#hasTrailerPosterSource() &&
      !this._isPlayable &&
      !this._hasSeenLiveActive;

    this.#setControllerData(
      'trailer-active',
      shouldShowTrailer ? 'true' : null,
    );
  }
  #isDebugLoggingEnabled(): boolean {
    if (this.getAttribute('debug') === 'true') return true;
    return isDebugEnabled();
  }

  #debugLogUnexpectedError(
    context: string,
    error: unknown,
    expectedErrorNames: string[] = [],
  ): void {
    const errorName = error instanceof Error ? error.name : '';
    if (expectedErrorNames.includes(errorName)) return;
    if (!this.#isDebugLoggingEnabled()) return;
    console.debug(`[BunnyStreamVideo] ${context}`, error);
  }

  /**
   * Re-issue a full source load after the playlist 404'd because it had not
   * been published yet. Driven by the live-status poll rather than a timer of
   * its own: the poll already runs on the right cadence, already knows whether
   * the stream is still expected to be playable, and already pauses with the
   * tab. A full `load()` is required because hls.js's `startLoad()` only
   * resumes level and fragment loading - with no parsed multivariant manifest
   * there is no level to resume and it silently requests nothing.
   *
   * `load()` runs the media element's load algorithm, which pauses the element
   * and rejects any in-flight play() with an AbortError. The URL is unchanged,
   * so #applyLiveState's source-change auto-resume does not cover this - carry
   * the playback intent across the reload here or a viewer who already pressed
   * play is left paused once the playlist appears.
   */
  #reloadSourceIfPending(): void {
    if (!this._needsSourceReload) return;
    if (!this._isPlayable) {
      // The stream is no longer allowed to play, so this retry is abandoned.
      // Clearing the awaiting state matters as much as the reload flag: no
      // manifest can parse once the source is gone, so nothing else would ever
      // clear it, and the cadence would stay fast over a retry that no longer
      // exists.
      this.#clearPlaylistRetry();
      return;
    }
    if (!this.isConnected) return;
    if (!this.getAttribute('src')) return;
    if (this._playlistProbeInFlight) return;

    const probeUrl = this.#pendingPlaylistUrl();
    if (!probeUrl) return;

    // Probe before reloading. `load()` destroys and rebuilds the whole hls.js
    // instance, so reloading on every poll to find out whether the playlist
    // exists yet costs a teardown per poll - and at the fast cadence a
    // broadcaster who takes a minute to go live pays ~60 of them. A probe is
    // one small request, and the rebuild then happens once, when it will
    // succeed. `_needsSourceReload` stays set until then, so a probe that
    // finds nothing simply leaves the retry outstanding for the next poll.
    this._playlistProbeInFlight = true;
    probeLivePlaylist(probeUrl)
      .then((available) => {
        if (!available || !this._needsSourceReload) return;
        if (!this.isConnected) return;
        // The pending playlist may have changed under the probe - a live -> VOD
        // source swap, or a different rendition failing - which makes this
        // result about a URL nobody is waiting on any more.
        if (this.#pendingPlaylistUrl() !== probeUrl) return;
        this.#reloadSource();
      })
      .finally(() => {
        this._playlistProbeInFlight = false;
      });
  }

  /**
   * The playlist a pending retry is waiting on.
   *
   * The failed URL rather than the source, because `isPlaylistNotFoundError`
   * also covers level and audio-track playlists: when one of those 404s while
   * the multivariant manifest is fine, probing the source would succeed
   * immediately and rebuild hls.js on every poll - exactly the churn the probe
   * exists to remove. Falls back to the source for an error that carried no
   * URL, which is the only playlist known to be involved in that case.
   */
  #pendingPlaylistUrl(): string | null {
    return this._awaitingPlaylistUrl ?? this.getAttribute('src');
  }

  /** Forget a pending playlist retry, in whichever way it ended. */
  #clearPlaylistRetry(): void {
    this._needsSourceReload = false;
    this._awaitingPlaylist = false;
    this._awaitingPlaylistUrl = null;
    // The polls spent on this retry must not count towards the next one: a
    // fresh 404 before the next poll would otherwise park without its grace.
    this._outstandingRetryPolls = 0;
    this.#syncLiveStoppedState();
  }

  /**
   * Record that a playlist load 404'd and a retry is now outstanding.
   *
   * The cadence for the next poll was chosen before this load was even issued,
   * so the pending timer is replaced too: a stream that becomes playable on one
   * poll gets its source attached after that poll picked the configured
   * interval, and without rescheduling the first recovery probe - and so
   * playback - would wait out that whole interval.
   */
  #markPlaylistRetryPending(failedUrl: string | null): void {
    this._needsSourceReload = true;
    this._awaitingPlaylist = true;
    this._awaitingPlaylistUrl = failedUrl;

    this._nextPollDelayMs = resolveLiveStatusPollDelay({
      baseIntervalMs: this._liveStatusInterval,
      isAwaitingPlaylist: true,
      outstandingRetryPolls: this._outstandingRetryPolls,
      isPreviewPlayer: this._livePreview,
    });

    // Only when a poll is already scheduled: no timer means polling is stopped,
    // the tab is hidden, or a poll is in flight and will schedule the next one
    // itself - picking up the delay just set.
    if (this._liveStatusTimer) {
      this.#scheduleNextPoll();
    }
  }

  #reloadSource(): void {
    const wasPlaying = !this.paused;
    this._needsSourceReload = false;
    Promise.resolve(this.load())
      .then(() => {
        if (!wasPlaying || !this.isConnected) return;
        return this.play?.();
      })
      .catch((error) => {
        this.#debugLogUnexpectedError('Source reload failed', error, [
          'NotAllowedError',
          'AbortError',
        ]);
      });
  }

  #setMediaSrc(nextSrc: string | null): boolean {
    if (!nextSrc) return false;
    if (this.#shouldAttachSource(this.getAttribute('src'), nextSrc)) {
      // The src change triggers its own load(); drop any pending retry so the
      // poll does not reload the freshly set source a second time.
      this.#clearPlaylistRetry();
      this.setAttribute('src', nextSrc);
      return true;
    }
    return false;
  }

  /**
   * The live status poll re-signs the playlist and thumbnail on every
   * response, so for a live source a newer signing of the same URL is the same
   * source. Other sources are only set from outside, and any new URL counts.
   */
  #isSameSource(
    a: string | null | undefined,
    b: string | null | undefined,
  ): boolean {
    return this.#isLiveManagedSource() ? isSameSignedSource(a, b) : a === b;
  }

  /**
   * Attaching each signing would rebuild playback every poll, so a newer
   * signing only replaces the attached one before its token lapses.
   */
  #shouldAttachSource(current: string | null, next: string): boolean {
    if (current === next) return false;
    return !this.#isSameSource(current, next) ||
      isSignedSourceExpiring(current);
  }

  /**
   * Keep the latest signing of the source, so a later swap uses a fresh token,
   * but for a live source only report a change when it names a different
   * playlist.
   */
  /**
   * The attached token was refused before the refresh margin swapped it: the
   * viewer's clock runs behind the API's, or the poll interval is longer than
   * the margin. The status poll keeps the latest signing in `_contentSrc`, so
   * attach that once, carrying the viewer's playback across the reload.
   */
  #reattachNewerSigning(): boolean {
    if (this._forbiddenReattachPending) return false;
    const attached = this.getAttribute('src');
    const latest = this._contentSrc;
    if (!attached || !latest || attached === latest) return false;
    if (!isSameSignedSource(attached, latest)) return false;

    const wasPlaying = !this.paused;
    this._forbiddenReattachPending = true;
    this.#clearPlaylistRetry();
    this.setAttribute('src', latest);
    if (wasPlaying) {
      this.play?.().catch((error) => {
        this.#debugLogUnexpectedError(
          'Failed to resume playback after re-attaching the source',
          error,
          ['NotAllowedError', 'AbortError'],
        );
      });
    }
    return true;
  }

  #setContentSrc(nextSrc: string | null | undefined): boolean {
    const normalizedSrc = this.#normalizeContentSrc(nextSrc);
    const previousSrc = this._contentSrc;
    if (previousSrc === normalizedSrc) return false;

    this._contentSrc = normalizedSrc;
    this.#syncCastSrc();
    return !this.#isSameSource(previousSrc, normalizedSrc);
  }

  /**
   * castable-video reads `title`/`poster` off the element when it builds the
   * Chromecast now-playing metadata. Source them from `cast-title` /
   * `cast-poster` so the native tooltip (title attribute) and the native
   * <video> poster (which would fight the bunny-poster-image slot) stay
   * untouched.
   */
  override get title(): string {
    return this.getAttribute('cast-title') ?? super.title;
  }

  override set title(value: string) {
    super.title = value;
  }

  override get poster(): string {
    const castPoster = this.getAttribute('cast-poster');
    if (castPoster !== null) return castPoster;
    // custom-media-element's attribute reflection returns `false` (not null)
    // for a missing poster attribute; normalize to a string for castable.
    const inherited = super.poster as unknown;
    return typeof inherited === 'string' ? inherited : '';
  }

  override set poster(value: string) {
    super.poster = value;
  }

  _explicitCastCustomData: Record<string, unknown> | undefined;

  /**
   * castable-video sends this object as the Chromecast LoadRequest
   * customData. The Bunny receiver reads `drm.widevine.licenseUrl` from it
   * to configure Widevine playback for eDRM content (without it DRM casts
   * fail on the device) and `style` to theme itself to match the embed.
   * Shadows the mixin's accessor so values (the token-authenticated license
   * URL, the current computed theming) are captured at cast time.
   */
  get castCustomData(): Record<string, unknown> | undefined {
    if (this._explicitCastCustomData) return this._explicitCastCustomData;

    const customData: Record<string, unknown> = {};

    if (this._edrmEnabled && this._drmProvider === 'widevine') {
      customData.drm = {
        widevine: {
          licenseUrl: this.#getWidevineLicenseUrl(),
          withCredentials: false,
        },
      };
    }

    const style = this.#getCastStyle();
    if (style) customData.style = style;

    return Object.keys(customData).length > 0 ? customData : undefined;
  }

  set castCustomData(value: Record<string, unknown> | undefined) {
    this._explicitCastCustomData = value;
  }

  /**
   * Receiver theming is derived from the same CSS custom properties that
   * style the local player (media-chrome convention), so the TV mirrors the
   * embed and theming the player once via CSS is all that is ever needed.
   * See client/src/cast-receiver/README.md for the full contract.
   *
   * Key color: the local progress bar is themed by --media-range-bar-color,
   * but that is scoped to bunny-media-time-range and does not inherit to
   * this element, so it is only picked up here when someone sets it at an
   * inheritable scope; --media-secondary-color (set on media-controller
   * from the same playerKeyColor value) is the fallback that covers the
   * stock stylesheet.
   *
   * Caption font size: the computed --captions-font-size is what the local
   * player currently renders, which varies with the embed's responsive
   * breakpoint — the TV deliberately mirrors the embed at cast time.
   */
  #getCastStyle(): Record<string, unknown> | null {
    let computed: CSSStyleDeclaration;
    try {
      computed = getComputedStyle(this);
    } catch (_error) {
      return null;
    }
    const read = (name: string): string | null => {
      const value = computed.getPropertyValue(name).trim();
      return value.length > 0 ? value : null;
    };

    const style: Record<string, unknown> = {};

    const keyColor = read('--media-range-bar-color') ??
      read('--media-secondary-color');
    if (keyColor) style.keyColor = keyColor;

    const captions: Record<string, string> = {};
    const fontColor = read('--captions-text-color');
    if (fontColor) captions.fontColor = fontColor;
    const background = read('--captions-background');
    if (background) captions.background = background;
    const fontSize = read('--captions-font-size');
    if (fontSize) captions.fontSize = fontSize;
    // The full fallback chain ships raw; the receiver extracts the library
    // font (first entry) and rejects the chain when none is configured.
    const fontFamily = read('--captions-font-family');
    if (fontFamily) captions.fontFamily = fontFamily;
    if (Object.keys(captions).length > 0) style.captions = captions;

    return Object.keys(style).length > 0 ? style : null;
  }

  _castAudioTrackList: LocalAudioTrackListLike | null = null;

  #handleCastAudioTracksChange = (): void => {
    this.#syncCastAudioTrack();
  };

  #getLocalAudioTrackList(): LocalAudioTrackListLike | null {
    const tracks = (this as unknown as { audioTracks?: unknown }).audioTracks;
    return tracks instanceof EventTarget
      ? tracks as LocalAudioTrackListLike
      : null;
  }

  #attachCastAudioTrackListener(): void {
    if (this._castAudioTrackList) return;
    const list = this.#getLocalAudioTrackList();
    if (!list) return;
    list.addEventListener('change', this.#handleCastAudioTracksChange);
    this._castAudioTrackList = list;
  }

  #detachCastAudioTrackListener(): void {
    this._castAudioTrackList?.removeEventListener(
      'change',
      this.#handleCastAudioTracksChange,
    );
    this._castAudioTrackList = null;
  }

  #getEnabledLocalAudioTrack(): LocalAudioTrackLike | null {
    const list = this.#getLocalAudioTrackList();
    if (!list) return null;
    for (let i = 0; i < list.length; i++) {
      const track = list[i];
      if (track?.enabled) return track;
    }
    return null;
  }

  // The castable-video RemotePlayback object is recreated per connection to
  // the DOM, so keep the ref the listeners were attached to.
  _castRemoteTarget: EventTarget | null = null;

  #handleRemoteConnect = (): void => {
    this.#syncCastingOverlay(true);
    // ONLY for resumed sessions: a fresh cast pauses local playback via
    // castable-video's prompt path, which must capture the pre-cast
    // playing state FIRST — it becomes the LoadRequest's autoplay, so
    // suppressing local playback on every connect would start the TV
    // paused whenever the video was playing when casting began.
    if (isCastSessionResumed()) {
      this.#suppressLocalPlayback();
    }
    this._castMediaWasLoaded = false;
    this._castMediaOverNotified = false;
    this.#attachCastMediaInfoListener();
  };
  #handleRemoteDisconnect = (): void => {
    this.#syncCastingOverlay(false);
    this._castMediaWasLoaded = false;
    this._castMediaOverNotified = false;
    this.#detachCastMediaInfoListener();
  };

  /**
   * A resumed cast session (page reload while casting) never goes through
   * castable-video's prompt path — the only place it pauses local playback —
   * so with the `autoplay` attribute the browser would keep playing audibly
   * underneath the cast overlay, in parallel with the TV. Pause the NATIVE
   * element: while casting, the element-level pause() forwards to the
   * receiver and would pause the TV instead. Clearing native autoplay stops
   * a still-loading media element from starting playback after this runs
   * (castable-video's disconnect handler resumes local playback explicitly,
   * so the flag isn't needed again).
   */
  #suppressLocalPlayback(): void {
    const nativeEl = this.nativeEl;
    if (!nativeEl) return;
    nativeEl.autoplay = false;
    if (!nativeEl.paused) nativeEl.pause();
  }

  _castMediaInfoUnsubscribe: (() => void) | null = null;

  /**
   * The post-load audio reconcile races the receiver's first MediaStatus —
   * the track list arrives asynchronously after the LoadRequest, so a
   * pre-cast local audio selection was silently dropped and the TV started
   * on its default track. Re-run the (idempotent) reconcile whenever the
   * session's media info changes; it no-ops once the selection matches.
   */
  // Tracks the finished-notification so 'pause' fires once per ending.
  _castMediaOverNotified = false;

  #attachCastMediaInfoListener(): void {
    if (this._castMediaInfoUnsubscribe) return;
    this._castMediaInfoUnsubscribe = onCastMediaInfoChanged(() => {
      if (getCastMediaSession()) this._castMediaWasLoaded = true;
      if (this.#isCastMediaOver()) {
        // media-chrome only re-reads `paused` on play/pause events, and
        // castable-video dispatches neither when the receiver finishes —
        // without this the control bar keeps showing the pause icon.
        if (!this._castMediaOverNotified) {
          this._castMediaOverNotified = true;
          this.dispatchEvent(new Event('pause'));
          this.dispatchEvent(new Event('ended'));
        }
      } else {
        this._castMediaOverNotified = false;
      }
      this.#syncCastAudioTrack();
      this.#syncCastPlaybackRate();
    });
  }

  #detachCastMediaInfoListener(): void {
    this._castMediaInfoUnsubscribe?.();
    this._castMediaInfoUnsubscribe = null;
  }

  // Set once a cast media session has been observed, so a null media
  // session means "over" rather than "first load still in flight".
  _castMediaWasLoaded = false;

  /**
   * Whether the cast session is connected but its media session is over —
   * the receiver reached the end of the video (IDLE/FINISHED) or the media
   * is gone entirely. In that state castable-video's play()/pause()/seek
   * all forward into the void (playOrPause() against dead media is a
   * silent no-op), which reads as a frozen player.
   */
  #isCastMediaOver(): boolean {
    if (this.remote?.state !== 'connected') return false;
    const media = getCastMediaSession();
    if (!media) return this._castMediaWasLoaded;
    return media.playerState === 'IDLE' && !!media.idleReason;
  }

  /**
   * castable-video's `paused` getter proxies the RemotePlayer's isPaused,
   * which stays FALSE after the receiver's media session ends — so the
   * control bar keeps showing the pause icon and clicking it sends a
   * pause into the dead session (silent no-op) instead of ever reaching
   * play(). Report the finished cast as paused so the UI offers play,
   * which then triggers the replay load below.
   */
  override get paused(): boolean {
    if (this.#isCastMediaOver()) return true;
    return super.paused;
  }

  /**
   * Replay support while casting: after the receiver finishes the video
   * its media session is dead and castable-video has no re-load handling,
   * so the play button would do nothing. Send a fresh LoadRequest instead —
   * castable-video autoplays it when the pre-cast state was "playing".
   */
  override async play(): Promise<void> {
    if (this.#isCastMediaOver()) {
      try {
        await this.load();
      } catch (error) {
        this.#debugLogUnexpectedError('castReplayLoad', error);
      }
    }
    return super.play();
  }

  /**
   * CMCD v2 reporting config (STREAM-1020), enabled by the server-set `cmcd`
   * attribute (library flag `player-cmcd`, `?cmcd=` override). Request mode
   * rides on every playlist/fragment URL as `?CMCD=` and lands in the CDN
   * access logs; event mode POSTs CTA-5004-B reports (time interval, play
   * state, errors, bitrate changes) to the same-origin /cmcd collector,
   * which proxies them to the analytics backend. The session id is minted
   * per page load (NOT hls.js's per-instance default) so trailer reloads,
   * error recoveries and cast returns stay one session in the pipeline.
   */
  // Context travels as query params: pullZoneId (required - the analytics
  // pipeline is keyed by pull zone, and /stream/analytics/cmcd 400s without
  // it) and cid (redundant with the payload, but access logs and routing
  // can't see POST bodies). videoLibraryId rides along as optional,
  // diagnostics-only context - never a substitute for a missing pullZoneId,
  // which some play data responses still don't carry. Keeping the path a
  // bare /cmcd lets the same contract serve other hosts later. Shared by
  // both the hls.js-driven event beacon (#buildCmcdConfig) and the
  // standalone waiting-viewer beacon (#sendWaitingCmcdBeacon), which posts
  // independently of hls.js since hls.js never attaches while a live stream
  // isn't playable.
  #buildCmcdBeaconUrl(videoId: string, libraryId: string): URL {
    return buildCmcdBeaconUrl(globalThis.location.href, videoId, {
      pullZoneId: this.getAttribute('pullzone-id'),
      videoLibraryId: libraryId,
    });
  }

  /**
   * Fires a CMCD v2 custom event (`e=ce,cen="waiting"`) while polling for a
   * live stream that hasn't started, so the analytics pipeline can count
   * viewers currently waiting. Hand-built and posted directly rather than
   * routed through hls.js's own CMCDController/eventTargets, since hls.js
   * never attaches to a source (hence never constructs a CMCDController)
   * while the stream isn't playable. Gated on the same `cmcd` attribute as
   * #buildCmcdConfig() - this is a real CMCD v2 event, not an independent
   * presence signal. Fire-and-forget: a failed beacon must never affect live
   * status polling. Throttled to WAITING_CMCD_BEACON_INTERVAL_MS,
   * independent of the (much more frequent) live-status poll that calls
   * this - per-poll granularity isn't needed for this metric, and fewer
   * requests eases scaling.
   */
  #sendWaitingCmcdBeacon(): void {
    if (this.getAttribute('cmcd') !== 'true') return;
    const videoId = this._videoId;
    const libraryId = this._libraryId;
    if (!videoId || !libraryId) return;

    const now = Date.now();
    if (
      !shouldSendWaitingCmcdBeacon(
        this.#lastWaitingCmcdBeaconAt,
        now,
        WAITING_CMCD_BEACON_INTERVAL_MS,
      )
    ) {
      return;
    }
    this.#lastWaitingCmcdBeaconAt = now;

    const beaconUrl = this.#buildCmcdBeaconUrl(videoId, libraryId);
    const line = buildWaitingCmcdLine(getPlaybackSessionId(), videoId);

    fetch(beaconUrl.toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/cmcd' },
      body: line,
      keepalive: true,
    }).catch(() => {
      // Best-effort presence ping; failures are silently swallowed.
    });
  }

  #buildCmcdConfig(): Record<string, unknown> | null {
    if (this.getAttribute('cmcd') !== 'true') return null;
    const videoId = this._videoId;
    const libraryId = this._libraryId;
    if (!videoId || !libraryId) return null;

    const beaconUrl = this.#buildCmcdBeaconUrl(videoId, libraryId);

    return {
      version: 2,
      contentId: videoId,
      sessionId: getPlaybackSessionId(),
      eventTargets: [{
        url: beaconUrl.toString(),
        events: ['t', 'ps', 'e', 'bc'],
        interval: 30,
      }],
      // keepalive lets the end-of-session flush survive page unload; the
      // reporter only needs the status back (410 dispose, 429/5xx retry).
      loader: (request: {
        url: string;
        method?: string;
        headers?: Record<string, string>;
        body?: BodyInit;
      }) =>
        fetch(request.url, {
          method: request.method,
          headers: request.headers,
          body: request.body,
          keepalive: true,
        }),
    };
  }

  #handleRateChange = (): void => {
    this.#syncCastPlaybackRate();
  };

  /**
   * Mirror the local playback rate to an active cast session — the speed
   * menu keeps setting the (paused) local element's rate while casting, so
   * without this it would silently change nothing on the TV. Uses the
   * standard SET_PLAYBACK_RATE media command (what the Android/iOS sender
   * SDKs emit), and the local rate stays authoritative: stopping the cast
   * resumes local playback at the same speed. Idempotent — the receiver's
   * MediaStatus echoes the applied rate back into `playbackRate`.
   */
  #syncCastPlaybackRate(): void {
    if (this.remote?.state !== 'connected') return;
    const mediaSession = getCastMediaSession();
    // A finished media session (IDLE) can't take rate commands.
    if (!mediaSession || mediaSession.playerState === 'IDLE') return;
    const rate = this.playbackRate;
    if (!Number.isFinite(rate) || rate <= 0) return;
    if (mediaSession.playbackRate === rate) return;
    setCastPlaybackRate(
      rate,
      (error) => this.#debugLogUnexpectedError('castPlaybackRate', error),
    );
  }

  /**
   * Mirror the cast session state onto the controller's data-casting
   * attribute — the single signal that drives the casting CSS state and
   * bunny-cast-overlay (which self-serves everything else, including the
   * device name, from the cast SDK).
   */
  #syncCastingOverlay(active: boolean): void {
    this.#setControllerData('casting', active ? 'true' : null);
    this._isCasting = active;
    // A parked stream hides the controls that pause or stop the receiver.
    this.#syncLiveStoppedState();
  }

  #attachCastStateListeners(): void {
    // Chromecast only — on Safari `remote` is the native RemotePlayback and
    // its connect events belong to AirPlay, which has its own native UI.
    if (this._castRemoteTarget) return;
    if (this.getAttribute('chromecast') !== 'true' || !isChrome()) return;

    const remote = this.remote;
    if (!(remote instanceof EventTarget)) return;
    remote.addEventListener('connect', this.#handleRemoteConnect);
    remote.addEventListener('disconnect', this.#handleRemoteDisconnect);
    this._castRemoteTarget = remote;
    // Speed-menu changes surface as ratechange on this element; the sync
    // no-ops unless a cast session is connected.
    this.addEventListener('ratechange', this.#handleRateChange);
  }

  #detachCastStateListeners(): void {
    this._castRemoteTarget?.removeEventListener(
      'connect',
      this.#handleRemoteConnect,
    );
    this._castRemoteTarget?.removeEventListener(
      'disconnect',
      this.#handleRemoteDisconnect,
    );
    this._castRemoteTarget = null;
    this.removeEventListener('ratechange', this.#handleRateChange);
    this.#detachCastMediaInfoListener();
  }

  /**
   * Mirror the local audio-track selection to an active Chromecast session.
   * castable-video only bridges text tracks to the receiver; without this,
   * the audio menu would switch the paused local player while the TV keeps
   * playing the previous language. Matching is by language/name because
   * local hls.js track order need not equal the receiver's manifest order.
   */
  #syncCastAudioTrack(): void {
    // Only the element that owns the cast session may edit receiver tracks:
    // the media session is a page-global, so without this an audio change
    // on a non-casting player could switch the TV's language.
    if (this.remote?.state !== 'connected') return;

    const mediaSession = getCastMediaSession();
    // A finished media session (IDLE) can't take track edits.
    if (!mediaSession || mediaSession.playerState === 'IDLE') return;

    const localTrack = this.#getEnabledLocalAudioTrack();
    if (!localTrack) return;

    const receiverTrack = pickReceiverAudioTrack(
      mediaSession.media?.tracks,
      localTrack.language ?? null,
      localTrack.label ?? null,
    );
    if (!receiverTrack) return;
    if (mediaSession.activeTrackIds?.includes(receiverTrack.trackId)) return;

    const EditTracksInfoRequest = getEditTracksInfoRequest();
    if (!EditTracksInfoRequest) return;

    const activeTrackIds = buildActiveTrackIds(
      mediaSession.media?.tracks,
      mediaSession.activeTrackIds,
      receiverTrack.trackId,
    );

    // The session can be torn down between the lookup above and this call;
    // never let that escape the audioTracks change listener.
    try {
      mediaSession.editTracksInfo(
        new EditTracksInfoRequest(activeTrackIds),
        () => {},
        (error) => this.#debugLogUnexpectedError('castAudioTrackSwitch', error),
      );
    } catch (error) {
      this.#debugLogUnexpectedError('castAudioTrackSwitch', error);
    }
  }

  #syncCastSrc(): void {
    const castSrc = this._contentSrc;

    if (castSrc) {
      // castable-video reloads the receiver on every cast-src change.
      if (this.#shouldAttachSource(this.getAttribute('cast-src'), castSrc)) {
        this.setAttribute('cast-src', castSrc);
      }
      return;
    }

    if (this.hasAttribute('cast-src')) {
      this.removeAttribute('cast-src');
    }
  }

  #updateCountdownState(
    scheduledTime: string | null,
    enableCountdown: boolean,
  ): void {
    const controller = this.#getMediaController();
    if (!controller) return;

    const countdown = controller.querySelector('live-stream-countdown');
    const shouldShow = Boolean(enableCountdown && scheduledTime);

    if (countdown) {
      if (shouldShow) {
        countdown.setAttribute('scheduled-time', scheduledTime ?? '');
      } else {
        countdown.removeAttribute('scheduled-time');
      }
    }

    this.#setControllerData('countdown', shouldShow ? 'true' : null);
  }

  #isTrailerActive(): boolean {
    return this.#getMediaController()?.dataset?.trailerActive === 'true';
  }

  /**
   * Recomputed rather than set directly: the poster can report that its artwork
   * is unusable at any point, which has to put a suppressed message back on
   * screen without waiting for the next poll.
   */
  #syncStatusOverlay(): void {
    const shouldShow = shouldShowStatusOverlay({
      showStatus: this._liveShowStatus,
      statusMessage: this._liveStatusMessage,
      posterArtworkFailed: this._posterArtworkFailed,
      trailerActive: this.#isTrailerActive(),
    });
    this.#setControllerData('status', shouldShow ? 'true' : null);
  }

  #attachPosterArtworkListener(): void {
    if (this._posterArtworkListenerAttached) return;
    const controller = this.#getMediaController();
    if (!controller) return;

    this._posterArtworkHandler = (event: Event) => {
      const available = (event as CustomEvent<{ available?: boolean }>).detail
        ?.available;
      this._posterArtworkFailed = available === false;
      this.#syncStatusOverlay();
    };
    controller.addEventListener(
      POSTER_ARTWORK_STATE_EVENT,
      this._posterArtworkHandler,
    );
    this._posterArtworkListenerAttached = true;
  }

  #detachPosterArtworkListener(): void {
    if (!this._posterArtworkListenerAttached) return;
    const controller = this.#getMediaController();
    if (controller && this._posterArtworkHandler) {
      controller.removeEventListener(
        POSTER_ARTWORK_STATE_EVENT,
        this._posterArtworkHandler,
      );
    }
    this._posterArtworkHandler = null;
    this._posterArtworkListenerAttached = false;
  }

  #updateStatusMessage(statusMessage: string | null | undefined): void {
    const controller = this.#getMediaController();
    if (!controller) return;
    const statusMessageNode = controller.querySelector(
      '.offline-overlay[data-overlay="status"] .offline-message',
    );
    if (!(statusMessageNode instanceof HTMLElement)) return;
    statusMessageNode.textContent = statusMessage || '';
  }

  /** Same rule as the media source: a re-signed thumbnail is not a new one. */
  #setImageSrc(image: Element, src: string): void {
    if (this.#shouldAttachSource(image.getAttribute('src'), src)) {
      image.setAttribute('src', src);
    }
  }

  #updatePosterThumbnail(thumbnailUrl: string | null | undefined): void {
    const update = resolveThumbnailPosterUpdate(thumbnailUrl);
    if (update.action === 'keep') return;

    const controller = this.#getMediaController();
    if (!controller) return;

    const posterImage = controller.querySelector(
      'bunny-poster-image[slot="poster"]',
    );
    if (posterImage instanceof HTMLElement) {
      if (update.action === 'set') {
        this.#setImageSrc(posterImage, update.src);
      } else {
        // Blurhash first: the poster keeps itself visible for as long as that
        // attribute is set, so dropping only the src would leave the stale
        // placeholder on screen under the restored message. Live posters carry
        // no blurhash today - this keeps "clear" honest if they ever do.
        posterImage.removeAttribute('blurhash');
        posterImage.removeAttribute('src');
      }
    }

    // The parked-stream poster mirrors the thumbnail too: it is the artwork a
    // viewer sees once an active stream's ingest disappears (STREAM-922).
    const stoppedPoster = controller.querySelector('.live-stopped-poster');
    const stoppedImage = stoppedPoster?.querySelector('img');
    if (stoppedPoster instanceof HTMLElement && stoppedImage) {
      if (update.action === 'set') {
        this.#setImageSrc(stoppedImage, update.src);
      } else {
        stoppedImage.removeAttribute('src');
      }
      this.#watchStoppedPoster();
      this.#reflectStoppedPosterArtwork();
    }

    // Trailer poster intentionally does not mirror thumbnail to avoid pre-stream thumbnail flashes.
  }

  /**
   * Park the player while an active stream's playlist has been missing for
   * longer than an ingest failover takes (STREAM-922): the thumbnail when the
   * stream has one, otherwise the last frame, with the spinner and the dead
   * controls taken off it. Nothing is paused or torn down, so the retry that
   * is already outstanding brings playback back on its own. Cleared the
   * moment that retry ends, in whichever way: the playlist is back, the
   * stream ended and the status overlay takes over, or the source changed.
   *
   * hls.js only, like the retry it hangs off: on the opt-in `native-hls` path
   * Safari refreshes the live playlist itself and nothing marks a retry
   * pending, so that player neither reloads nor parks.
   */
  #syncLiveStoppedState(): void {
    this.#watchStoppedPoster();
    const stopped = shouldParkOnThumbnail({
      isPlayable: this._isPlayable,
      hasSeenLiveActive: this._hasSeenLiveActive,
      awaitingPlaylist: this._awaitingPlaylist,
      outstandingRetryPolls: this._outstandingRetryPolls,
      isParked: this._isLiveStopped,
      isCasting: this._isCasting,
    });
    // Tap-to-play has nothing to start while parked; afterwards it follows
    // playability again, right away rather than on the next poll. Applied on
    // every sync, since each applied live state resets it from playability.
    this.#setControllerGesturesDisabled(stopped || !this._isPlayable);
    if (stopped === this._isLiveStopped) return;
    this._isLiveStopped = stopped;
    this.#setControllerData('live-stopped', stopped ? 'true' : null);
  }

  /**
   * The parked poster is artwork only once its thumbnail has loaded. A
   * thumbnail that fails to load leaves the attribute off, so the viewer
   * keeps the last frame rather than a black box. The server-rendered image
   * may have settled before this runs, which is why the state is read from
   * the element and not only from its events.
   */
  #watchStoppedPoster(): void {
    const image = this.#getMediaController()?.querySelector(
      '.live-stopped-poster img',
    );
    if (!(image instanceof HTMLImageElement)) return;
    if (image === this.#stoppedPosterImage) return;
    this.#stoppedPosterImage?.removeEventListener(
      'load',
      this.#reflectStoppedPosterArtwork,
    );
    this.#stoppedPosterImage?.removeEventListener(
      'error',
      this.#reflectStoppedPosterArtwork,
    );
    this.#stoppedPosterImage = image;
    image.addEventListener('load', this.#reflectStoppedPosterArtwork);
    image.addEventListener('error', this.#reflectStoppedPosterArtwork);
    this.#reflectStoppedPosterArtwork();
  }

  #reflectStoppedPosterArtwork = (): void => {
    const image = this.#stoppedPosterImage;
    const poster = image?.parentElement;
    if (!image || !poster) return;
    const loaded = image.hasAttribute('src') && image.complete &&
      image.naturalWidth > 0;
    if (loaded) {
      poster.setAttribute('data-artwork', 'true');
    } else {
      poster.removeAttribute('data-artwork');
    }
  };

  #setControllerGesturesDisabled(disabled: boolean): void {
    const controller = this.#getMediaController();
    if (!controller || controller?.dataset?.liveStream !== 'true') return;
    if (disabled) {
      controller.setAttribute('gesturesdisabled', '');
    } else {
      controller.removeAttribute('gesturesdisabled');
    }
  }

  #syncContentSrcToRuntimeSrc(): boolean {
    const contentSrc = this._contentSrc;

    if (!this.#isLiveManagedSource()) {
      if (!contentSrc) return false;
      return this.#setMediaSrc(contentSrc);
    }

    if (this._isPlayable) {
      if (!contentSrc) return false;
      return this.#setMediaSrc(contentSrc);
    }

    if (this.hasAttribute('src')) {
      this.removeAttribute('src');
      return true;
    }

    return false;
  }

  #applyLiveState({
    isPlayable = false,
    isLiveActive = false,
    showCountdown = false,
    scheduledStartTime = null,
    showStatus = false,
    statusMessage,
    // Left undefined on purpose when absent: a local re-sync must not be read as
    // "this stream has no thumbnail" and clear the rendered poster.
    thumbnailUrl,
    status,
    allowAutoResume = true,
  }: LiveStateOptions = {}): void {
    const wasPlaying = !this.paused;
    this._isPlayable = Boolean(isPlayable);
    this.#setControllerData('live', this._isPlayable ? 'true' : null);
    this.#setControllerData('live-active', isLiveActive ? 'true' : null);
    this.#syncTrailerActiveState(isLiveActive);
    this.#updateCountdownState(scheduledStartTime, showCountdown);
    if (statusMessage !== undefined && statusMessage !== null) {
      this._liveStatusMessage = String(statusMessage);
      this.#updateStatusMessage(this._liveStatusMessage);
    }
    this._liveShowStatus = Boolean(showStatus);
    this.#syncStatusOverlay();
    this.#updatePosterThumbnail(thumbnailUrl);
    this.#setControllerGesturesDisabled(!this._isPlayable);
    this.#syncLiveStoppedState();

    if (this._isPlayable) {
      const sourceChanged = this.#syncContentSrcToRuntimeSrc();
      if (wasPlaying && sourceChanged && allowAutoResume) {
        this.play?.().catch((error) => {
          this.#debugLogUnexpectedError(
            'Failed to auto-resume playback after source update',
            error,
            ['NotAllowedError', 'AbortError'],
          );
        });
      }
      return;
    }

    // The overlays alone can no longer stand in for "offline": a stream with
    // pre-stream artwork shows neither of them and still must not hold on to a
    // source it is not allowed to play. An unresolved status is the one case
    // that stays untouched - see isConfirmedOfflineLiveState.
    const isOfflineLiveState = isConfirmedOfflineLiveState({
      showCountdown,
      showStatus,
      status,
    });
    if (isOfflineLiveState && this.hasAttribute('src')) {
      this.pause?.();
      this.removeAttribute('src');
    }
  }

  #getLiveStateSnapshot(): LiveStateOptions {
    const controller = this.#getMediaController();
    const countdown = controller?.querySelector('live-stream-countdown');
    const scheduledStartTime = countdown?.getAttribute('scheduled-time') ||
      null;
    // The rendered overlay already holds the message for this state, including
    // one the server suppressed in favour of artwork - read it so a poster
    // failure before the first poll can still restore it.
    const statusMessage = controller?.querySelector(
      '.offline-overlay[data-overlay="status"] .offline-message',
    )?.textContent ?? undefined;

    return {
      isPlayable: controller?.dataset?.live === 'true',
      isLiveActive: controller?.dataset?.liveActive === 'true',
      showCountdown: controller?.dataset?.countdown === 'true',
      scheduledStartTime,
      showStatus: controller?.dataset?.status === 'true',
      statusMessage,
    };
  }

  #scheduleNextPoll(): void {
    if (this._liveStatusTimer) {
      clearTimeout(this._liveStatusTimer);
    }
    this._liveStatusTimer = setTimeout(() => {
      this._liveStatusTimer = null;
      this.#pollLiveStatus();
    }, this.#resolveNextPollDelay());
  }

  #resolveNextPollDelay(): number {
    // A null delay means nothing has been polled yet. The live embed HTML is
    // served with `max-age=5`, so the state rendered into the page can already
    // be stale on arrival - take one quick reading before settling into
    // whatever cadence that reading calls for. Routed through the same policy
    // for its jitter: viewers arriving for a scheduled start arrive as a wave
    // and would otherwise all fire this first poll on the same tick.
    return this._nextPollDelayMs ?? resolveLiveStatusPollDelay({
      baseIntervalMs: this._liveStatusInterval,
      isAwaitingPlaylist: true,
      outstandingRetryPolls: 0,
      isPreviewPlayer: this._livePreview,
    });
  }

  #isNonRetryableStatus(status: number): boolean {
    return status === 401 || status === 403 || status === 404 || status === 410;
  }

  async #pollLiveStatus() {
    if (!this.isConnected) return;
    if (!this._liveStatusUrl) return;
    if (!this.#isLiveStreamController()) return;
    if (this._liveStatusInFlight) {
      this.#scheduleNextPoll();
      return;
    }
    this._liveStatusInFlight = true;
    try {
      const pollUrl = new URL(this._liveStatusUrl, globalThis.location.href);
      if (document.referrer) {
        pollUrl.searchParams.set('referer', document.referrer);
      }

      const response = await fetch(pollUrl.toString(), {
        cache: 'no-store',
      });
      if (response.ok) {
        const data = await response.json() as LiveStatusResponse;
        const liveSrcChanged = this.#setContentSrc(data?.videoPlaylistUrl);
        const isPlayable = data?.isPlayable === true;
        const isLiveActive = data?.isLiveActive === true;
        const showCountdown = data?.showCountdown === true;
        const scheduledStartTime = data?.scheduledStartTime || null;
        const showStatus = data?.showStatus === true;
        const statusMessage = data?.statusMessage || '';
        const thumbnailUrl = data?.thumbnailUrl || null;

        if (isWaitingForLiveStreamStart(isPlayable, data?.status)) {
          this.#sendWaitingCmcdBeacon();
        }

        this.#updatePollCadence();

        this.#applyLiveState({
          isPlayable,
          isLiveActive,
          showCountdown,
          scheduledStartTime,
          showStatus,
          statusMessage,
          thumbnailUrl,
          status: data?.status,
          allowAutoResume: !(liveSrcChanged && isPlayable && !isLiveActive),
        });

        this.#reloadSourceIfPending();
      } else if (this.#isNonRetryableStatus(response.status)) {
        this.#stopLiveStatusPolling();
        return;
      } else {
        this.#backOffAfterPollFailure();
      }
    } catch (error) {
      this.#backOffAfterPollFailure();
      // Transient network failures are expected; log only unexpected cases in debug mode.
      this.#debugLogUnexpectedError('Live status polling failed', error, [
        'AbortError',
        'TypeError',
      ]);
    } finally {
      this._liveStatusInFlight = false;
    }

    if (
      this.isConnected &&
      document.visibilityState === 'visible'
    ) {
      this.#scheduleNextPoll();
    }
  }

  /** Decide how long to wait before the next poll, given what this one saw. */
  #updatePollCadence(): void {
    this._outstandingRetryPolls = this._awaitingPlaylist
      ? this._outstandingRetryPolls + 1
      : 0;

    this._nextPollDelayMs = resolveLiveStatusPollDelay({
      baseIntervalMs: this._liveStatusInterval,
      isAwaitingPlaylist: this._awaitingPlaylist,
      outstandingRetryPolls: this._outstandingRetryPolls,
      isPreviewPlayer: this._livePreview,
    });
    this.#syncLiveStoppedState();
  }

  /** Forget observed cadence state, so the next poll behaves like an arrival. */
  #resetPollCadence(): void {
    this._outstandingRetryPolls = 0;
    this._nextPollDelayMs = null;
  }

  /**
   * Fall back to the configured interval after a failed poll, rather than
   * staying on the fast cadence and adding load to an endpoint that is
   * already struggling. Set explicitly rather than via #resetPollCadence, so
   * a first poll that errors does not read as "never polled" and re-fire fast.
   *
   * The accumulated retry count deliberately survives. A failed poll is not
   * evidence that the retry is progressing, and zeroing it would hand out a
   * fresh set of fast polls after every transient 5xx - so intermittent
   * failures could keep a whole audience on the fast cadence indefinitely,
   * during exactly the origin incident MAX_FAST_RETRY_POLLS exists to bound.
   */
  #backOffAfterPollFailure(): void {
    this._nextPollDelayMs = this._liveStatusInterval;
  }

  #handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      if (this._liveStatusTimer) {
        clearTimeout(this._liveStatusTimer);
        this._liveStatusTimer = null;
      }
      // The viewer just came back and the stream may have started while the
      // tab was hidden, so treat this like a fresh arrival rather than
      // resuming a backoff that was measuring an unobserved stretch of time.
      this.#resetPollCadence();
      this.#pollLiveStatus();
      return;
    }
    if (this._liveStatusTimer) {
      clearTimeout(this._liveStatusTimer);
      this._liveStatusTimer = null;
    }
  }

  #startLiveStatusPolling(): void {
    this.#stopLiveStatusPolling();
    if (!this._liveStatusUrl) return;
    if (!this.#isLiveStreamController()) return;

    if (
      !this._liveStatusTimer &&
      !this._liveStatusInFlight &&
      document.visibilityState === 'visible'
    ) {
      this.#scheduleNextPoll();
    }

    if (!this._visibilityHandler) {
      this._visibilityHandler = this.#handleVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this._visibilityHandler);
    }
  }

  #stopLiveStatusPolling(): void {
    if (this._liveStatusTimer) {
      clearTimeout(this._liveStatusTimer);
      this._liveStatusTimer = null;
    }
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    // Nothing left to drive a retry once the poll is stopped.
    this.#clearPlaylistRetry();
    // A restart (reconnect, or a live-status attribute change) is a fresh
    // arrival: re-read quickly rather than trusting state observed earlier.
    this.#resetPollCadence();
  }

  #syncLiveState(): void {
    if (!this._liveStatusUrl) {
      this.#stopLiveStatusPolling();
      return;
    }
    const controller = this.#getMediaController();
    if (!controller || !this.#isLiveStreamController()) {
      this.#stopLiveStatusPolling();
      return;
    }
    this.#attachPosterArtworkListener();
    this.#applyLiveState(this.#getLiveStateSnapshot());
    this.#startLiveStatusPolling();
  }

  /**
   * Check if preload is enabled based on preload attribute
   * HTML5 preload values: "auto" (load entire video), "metadata" (load metadata only), "none" (no preload)
   * @returns {boolean} True if preload is "auto", false otherwise (none or metadata)
   */
  #shouldPreload(): boolean {
    const preloadAttr = this.getAttribute('preload');
    return preloadAttr === 'auto';
  }

  #syncWebkitPlaysinline(): void {
    const shouldUseWebkitPlaysinline =
      this.hasAttribute('disable-ios-player') ||
      this.hasAttribute('playsinline');

    if (shouldUseWebkitPlaysinline) {
      if (!this.hasAttribute('webkit-playsinline')) {
        this.setAttribute('webkit-playsinline', '');
      }
      return;
    }

    this.removeAttribute('webkit-playsinline');
  }

  #parseRememberPositionAttribute(value: string | null): boolean {
    if (value === null) return false;
    return value !== 'false';
  }

  #getRememberPositionStorageKey(): string | null {
    const videoId = this.getAttribute('video-id');
    return videoId ? `bunny_stream_position_${videoId}` : null;
  }

  #readRememberedPosition(storageKey: string | null): number | null {
    if (!storageKey) return null;

    try {
      const rawValue = globalThis.localStorage?.getItem(storageKey);
      if (!rawValue) return null;

      const parsed = Number.parseFloat(rawValue);
      return Number.isFinite(parsed) ? parsed : null;
    } catch (_error) {
      return null;
    }
  }

  #writeRememberedPosition(): void {
    if (!this._rememberPositionEnabled || !this._rememberPositionKey) return;
    if (!Number.isFinite(this.currentTime)) return;

    try {
      globalThis.localStorage?.setItem(
        this._rememberPositionKey,
        this.currentTime.toString(),
      );
    } catch (_error) {
      // Ignore storage failures such as private browsing restrictions.
    }
  }

  #clearRememberedPosition(): void {
    if (!this._rememberPositionKey) return;

    try {
      globalThis.localStorage?.removeItem(this._rememberPositionKey);
    } catch (_error) {
      // Ignore storage failures such as private browsing restrictions.
    }
  }

  #restoreRememberedPosition(): void {
    if (!this._rememberPositionEnabled) return;
    const rememberedPosition = this._pendingRememberedPosition;
    if (rememberedPosition == null || !Number.isFinite(rememberedPosition)) {
      return;
    }

    const duration = Number.isFinite(this.duration) ? this.duration : null;
    let nextTime = rememberedPosition;

    if (duration !== null) {
      nextTime = Math.min(nextTime, Math.max(duration - 1, 0));
    }

    this.currentTime = Math.max(nextTime, 0);
    this._pendingRememberedPosition = null;
  }

  #syncRememberPositionState(): void {
    this._rememberPositionEnabled = this.#parseRememberPositionAttribute(
      this.getAttribute('remember-position'),
    );
    this._rememberPositionKey = this.#getRememberPositionStorageKey();

    if (!this._rememberPositionEnabled || !this._rememberPositionKey) {
      this._pendingRememberedPosition = null;
      return;
    }

    this._pendingRememberedPosition = this.#readRememberedPosition(
      this._rememberPositionKey,
    );

    if (this.readyState > 0) {
      this.#restoreRememberedPosition();
    }
  }

  #attachRememberPositionListeners(): void {
    if (this._rememberPositionListenersAttached) return;

    this.addEventListener('loadedmetadata', () => {
      this.#restoreRememberedPosition();
    });
    this.addEventListener('timeupdate', () => {
      this.#writeRememberedPosition();
    });
    this.addEventListener('ended', () => {
      this.#clearRememberedPosition();
    });

    this._rememberPositionListenersAttached = true;
  }

  override attributeChangedCallback(
    name: string,
    oldVal: string | null,
    newVal: string | null,
  ): void {
    if (name === 'content-src') {
      const contentSrcChanged = this.#setContentSrc(newVal);
      if (contentSrcChanged) {
        if (this.#shouldAutoSyncContentSrc()) {
          if (this.#isLiveManagedSource()) {
            if (this.isConnected) {
              this.#applyLiveState(this.#getLiveStateSnapshot());
            }
          } else {
            this.#syncContentSrcToRuntimeSrc();
          }
        }
      }
    }
    if (name === 'disable-ios-player') {
      this.#syncWebkitPlaysinline();
    }
    if (name === 'remember-position' || name === 'video-id') {
      this.#syncRememberPositionState();
    }
    if (name === 'drm-version') {
      this._drmVersion = this.#parseDrmVersion(newVal);
      this._edrmEnabled = this._drmVersion === 1;
    }
    if (name === 'video-id') this._videoId = newVal;
    if (name === 'library-id') this._libraryId = newVal;
    if (name === 'drm-engine') this._drmEngine = newVal;
    if (name === 'base-url') {
      this._baseUrl = newVal || 'https://video.bunnycdn.com';
    }
    if (name === 'drm-provider') this._drmProvider = newVal;
    if (name === 'is-jit') {
      this.isJit = newVal === 'true';
    }
    if (name === 'level-cap') {
      this._levelCap = this.getAttribute('level-cap') === 'true';
    }
    if (name === 'widevine-sd-only-for-l3') {
      this._widevineSdOnlyForL3 = newVal === 'true';
      this._maxSoftwareWidevineResolution =
        resolveMaxSoftwareWidevineResolution(
          this.getAttribute('max-software-widevine-resolution'),
          this._widevineSdOnlyForL3,
        );
    }
    if (name === 'max-software-widevine-resolution') {
      this._maxSoftwareWidevineResolution =
        resolveMaxSoftwareWidevineResolution(
          newVal,
          this._widevineSdOnlyForL3,
        );
    }
    if (name === 'start-time') {
      this._startTime = parseFloat(newVal || '0');
    }
    if (name === 'chromecast') {
      this.#updateDisableRemotePlayback();
    }
    if (name === 'airplay') {
      this.#updateDisableRemotePlayback();
    }
    if (name === 'live-status-url') {
      this._liveStatusUrl = newVal;
    }
    if (name === 'live-status-interval') {
      this._liveStatusInterval = this.#parseLiveStatusInterval(newVal);
    }
    if (name === 'live-preview') {
      this._livePreview = newVal === 'true';
    }
    super.attributeChangedCallback?.(name, oldVal, newVal);

    if (
      name === 'live-status-url' ||
      name === 'live-status-interval' ||
      name === 'live-preview'
    ) {
      this.#syncLiveState();
    }
  }

  constructor() {
    super();
    this.mediaSourceSupported = this.#checkMediaSourceSupport();
    this.isJit = false;

    this._edrmEnabled = this._drmVersion === 1;

    this.#updateDisableRemotePlayback();

    // Make element focusable for keyboard events
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }
  }

  override connectedCallback(): void {
    super.connectedCallback?.();
    BunnyStreamVideoElement.warmCodecCapabilities();
    this.#syncCastSrc();
    this.#attachCastAudioTrackListener();
    this.#attachCastStateListeners();
    this.#attachRememberPositionListeners();
    this.#attachNativeMediaListeners();
    this.#syncWebkitPlaysinline();
    this.#syncRememberPositionState();
    this.#syncLiveState();
    if (!this.#isLiveManagedSource() && this.#shouldAutoSyncContentSrc()) {
      this.#syncContentSrcToRuntimeSrc();
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback?.();
    this.#detachCastAudioTrackListener();
    this.#detachCastStateListeners();
    this.#stopLiveStatusPolling();
    this.#detachPosterArtworkListener();
  }

  override init(): void {
    super.init();
    // Ensure shadow root exists (HlsVideoElement likely creates it, but confirm)
    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open' });
    }

    // When preload is disabled, HLS.js won't start loading automatically (autoStartLoad: false)
    // We need to start loading when the user initiates playback
    if (!this.#shouldPreload()) {
      // Start loading when play is triggered (added early to prevent race conditions)
      this.addEventListener('play', () => {
        if (this.api) {
          this.api.startLoad();
        }
      }, { once: true });
    }
  }

  async #isRobustnessSupported(
    robustness: WidevineRobustness,
    contentType = getWidevineVideoContentType('avc1.42E01E'),
  ): Promise<boolean> {
    try {
      await navigator.requestMediaKeySystemAccess('com.widevine.alpha', [{
        initDataTypes: ['cenc'],
        videoCapabilities: [{
          contentType,
          robustness,
        }],
      }]);
      return true;
    } catch (_error) {
      return false;
    }
  }

  async #getRobustness(): Promise<{ videoRobustness: WidevineRobustness }> {
    if (this.#shouldResolveWidevinePolicyByCodec()) {
      // HLS.js does not expose the manifest codecs until after construction.
      // The requestMediaKeySystemAccess hook resolves the real policy before
      // MediaKeys are created; this value is only a temporary config default.
      this._selectedWidevineRobustness = null;
      this._restrictWidevineToSd = false;
      return { videoRobustness: 'SW_SECURE_CRYPTO' };
    }

    const policy = await resolveWidevineRobustness({
      explicitRobustness: this.getAttribute('drm-video-robustness'),
      minimumSecurityLevel: this.getAttribute('drm-robustness'),
      sdOnlyForL3: this._widevineSdOnlyForL3,
      isSupported: (robustness) => this.#isRobustnessSupported(robustness),
    });
    this._selectedWidevineRobustness = policy.videoRobustness;
    this._restrictWidevineToSd = policy.restrictToSd;
    return { videoRobustness: policy.videoRobustness };
  }

  #shouldResolveWidevinePolicyByCodec(): boolean {
    return this._edrmEnabled &&
      this._drmProvider === 'widevine' &&
      this._widevineSdOnlyForL3;
  }

  async #requestCodecAwareWidevineAccess(
    keySystem: string,
    configurations: MediaKeySystemConfiguration[],
  ): Promise<MediaKeySystemAccess> {
    const requestMediaKeySystemAccess = navigator.requestMediaKeySystemAccess
      .bind(navigator);
    if (
      keySystem !== 'com.widevine.alpha' ||
      !this.#shouldResolveWidevinePolicyByCodec()
    ) {
      return requestMediaKeySystemAccess(keySystem, configurations);
    }

    const hls = this.api;
    if (!hls || hls.levels.length === 0) {
      return requestMediaKeySystemAccess(keySystem, configurations);
    }

    const wasLoading = hls.loadingEnabled;
    if (wasLoading) hls.stopLoad();

    try {
      const codecSupport = await probeWidevineCodecSupport(
        hls.levels.flatMap(({ videoCodec }) => videoCodec ? [videoCodec] : []),
        (probeConfigurations) =>
          requestMediaKeySystemAccess(keySystem, probeConfigurations),
        configurations[0],
      );
      const policy = await resolveWidevineCodecPolicy({
        levels: hls.levels,
        maxSoftwareResolution: this._maxSoftwareWidevineResolution,
        isSupported: (contentType, robustness) =>
          Promise.resolve(
            isWidevineCodecSupported(codecSupport, contentType, robustness),
          ),
      });
      const supportedConfigurations = applyWidevineCodecPolicyToConfigurations(
        configurations,
        policy,
      );

      if (
        policy.selectedCodecFamily === null ||
        supportedConfigurations.length === 0
      ) {
        this._selectedWidevineRobustness = 'SW_SECURE_CRYPTO';
        this._restrictWidevineToSd = true;
        return await requestMediaKeySystemAccess(
          keySystem,
          configurations,
        );
      }

      this._selectedWidevineRobustness = policy.videoRobustness;
      this._restrictWidevineToSd = policy.restrictToSd;
      hls.config.drmSystemOptions = {
        ...hls.config.drmSystemOptions,
        videoRobustness: policy.videoRobustness,
      };

      for (let index = policy.indicesToRemove.length - 1; index >= 0; index--) {
        hls.removeLevel(policy.indicesToRemove[index]);
      }

      // The policy left one codec family: rebuild the quality menu and the
      // start level for the pruned level list.
      this.#applyCodecFiltering(hls);
      const startLevel = this.#pickPreferredStartLevel(hls);
      if (startLevel >= 0) hls.startLevel = startLevel;

      return await requestMediaKeySystemAccess(
        keySystem,
        supportedConfigurations,
      );
    } finally {
      if (wasLoading && this.api === hls) hls.startLoad();
    }
  }

  #getManagedMediaSource(): typeof MediaSource | null {
    return (
      globalThis as typeof globalThis & {
        ManagedMediaSource?: typeof MediaSource;
      }
    ).ManagedMediaSource ?? null;
  }

  #checkMediaSourceSupport(): boolean {
    try {
      // Check for both MediaSource (desktop) and ManagedMediaSource (iOS 17.1+)
      return typeof MediaSource !== 'undefined' ||
        this.#getManagedMediaSource() !== null;
    } catch (_e) {
      return false;
    }
  }

  /**
   * Get the appropriate MediaSource API for codec support checking
   * @returns {typeof MediaSource | null} MediaSource or ManagedMediaSource API, or null if unavailable
   */
  #getMediaSourceAPI(): MediaSourceLike | null {
    if (typeof MediaSource !== 'undefined') return MediaSource;
    return this.#getManagedMediaSource();
  }

  /**
   * Check if the current page is loaded over HTTPS
   * @returns {boolean} True if HTTPS, false otherwise
   */
  #isSecureContext(): boolean {
    return globalThis.location?.protocol === 'https:' ||
      globalThis.location?.hostname === 'localhost' ||
      globalThis.location?.hostname === '127.0.0.1';
  }

  /**
   * Dispatch a player error event that can be caught by media-chrome or other listeners
   * @param {string} errorType - Type of error (e.g., 'drm', 'network', 'media')
   * @param {string} errorCode - Error code for translation lookup
   * @param {string} message - Human-readable error message
   * @param {object} details - Additional error details
   */
  #dispatchPlayerError(
    errorType: string,
    errorCode: string,
    message: string,
    details: Record<string, unknown> = {},
  ): void {
    const errorEvent = new CustomEvent('playererror', {
      bubbles: true,
      composed: true,
      detail: {
        type: errorType,
        code: errorCode,
        message,
        ...details,
      },
    });
    this.dispatchEvent(errorEvent);
    this.#playerErrorShown = true;

    // Also set the error on the video element for native error handling
    console.error(`[Player Error] ${errorType}: ${message}`, details);
  }

  /**
   * A load that got somewhere after an error was reported: a live retry, the
   * one-time manifest reload, or a new source. The overlay would otherwise
   * stay up, with the play button hidden under it, over a working video.
   */
  #clearPlayerError(): void {
    if (!this.#playerErrorShown) return;
    this.#playerErrorShown = false;
    this.dispatchEvent(
      new CustomEvent('playererrorcleared', { bubbles: true, composed: true }),
    );
  }

  /**
   * Use native HLS FairPlay on iOS/iPadOS. The server can miss iPad desktop-mode
   * UAs because they look like macOS Safari, so keep a runtime guard here too.
   */
  #shouldUseInternalFairPlay(): boolean {
    if (!this._edrmEnabled) return false;
    if (this._drmEngine === 'internal') return true;
    return this._drmProvider === 'fairplay' && isIOS();
  }

  /**
   * Update disableRemotePlayback based on chromecast and airplay attributes and browser type
   */
  #updateDisableRemotePlayback(): void {
    const chromecastDisabled = this.getAttribute('chromecast') !== 'true' &&
      isChrome();
    const airplayDisabled = this.getAttribute('airplay') === 'false' &&
      (isSafari() || isIOS());

    this.disableRemotePlayback = chromecastDisabled || airplayDisabled;
  }

  /**
   * Get the underlying HTMLVideoElement
   * @returns {HTMLVideoElement|null} The video element or null if not found
   */
  #getVideoElement(): HTMLVideoElement | null {
    // Check HLS.js api media reference first (most reliable)
    if (this.api && this.api.media) {
      return this.api.media instanceof HTMLVideoElement ? this.api.media : null;
    }

    // Fallback to shadow DOM
    if (this.shadowRoot) {
      const videoInShadow = this.shadowRoot.querySelector('video');
      if (videoInShadow) return videoInShadow;
    }

    // Last resort: direct child
    return this.querySelector('video');
  }

  /**
   * Setup start time seeking when video metadata loads using media-chrome seek request
   */
  #setupStartTimeSeeking(): void {
    const seekToStartTime = () => {
      this.dispatchEvent(
        new CustomEvent('mediaseekrequest', {
          composed: true,
          bubbles: true,
          detail: this._startTime,
        }),
      );
    };

    // If video is already ready, seek immediately
    if (this.readyState >= 1) {
      seekToStartTime();
    } else {
      // Wait for metadata to load
      this.addEventListener('loadedmetadata', seekToStartTime, { once: true });
    }
  }

  /**
   * Select the codec family for automatic playback and the quality menu from
   * hls.js's current level list. Levels stay in hls.js; see codec-filter.ts.
   */
  #applyCodecFiltering(hls: Hls): void {
    const levels = hls.levels ?? [];
    const selection = selectCodecFamily(
      levels,
      this.#getMediaSourceAPI(),
      BunnyStreamVideoElement.#codecCapabilities,
      {
        pickProbeLevel: (indices) =>
          this.#pickStartLevelAmong(
            indices.map((index) => ({
              ...levels[index],
              originalIndex: index,
            })),
            hls,
          ),
      },
    );
    this.#setFilteredLevels(
      selection.codecFamily === null ? null : selection.levelIndices.map((
        index,
      ) => ({
        ...levels[index],
        originalIndex: index,
        renditionId: String(
          this.#manifestRenditionIds.get(levels[index]) ?? index,
        ),
      })),
    );
  }

  #setFilteredLevels(levels: FilteredLevel[] | null): void {
    this._filteredLevels = levels;
    const rootNode = this.getRootNode();
    if (
      !(
        rootNode instanceof Document ||
        rootNode instanceof ShadowRoot ||
        rootNode instanceof Element
      )
    ) return;
    const renditionMenu = rootNode.querySelector(
      'bunny-rendition-menu',
    ) as RenditionMenuElement | null;
    if (renditionMenu && 'filteredLevels' in renditionMenu) {
      renditionMenu.filteredLevels = levels ?? [];
    }
  }

  #shouldLimitSoftwareWidevineLevels(): boolean {
    return this._edrmEnabled &&
      this._drmProvider === 'widevine' &&
      this._widevineSdOnlyForL3 &&
      this._maxSoftwareWidevineResolution !== null &&
      this._restrictWidevineToSd;
  }

  #pruneWidevinePlaybackLevels(
    hls: Hls,
    levels: CodecFilterCandidate[],
  ): boolean {
    if (!this.#shouldLimitSoftwareWidevineLevels()) return true;

    const indicesToRemove = getWidevinePlaybackLevelIndicesToRemove(
      levels,
      this._maxSoftwareWidevineResolution!,
    );

    if (indicesToRemove.length === levels.length) return false;
    for (let index = indicesToRemove.length - 1; index >= 0; index--) {
      hls.removeLevel(indicesToRemove[index]);
    }
    return true;
  }

  /**
   * Remove HEVC levels from HLS.js on Gecko-based browsers with broken HEVC seeking.
   * This browser workaround also excludes HEVC from automatic adaptation.
   * Returns false if the stream is HEVC-only and the
   * browser cannot handle it (caller should show an error).
   */
  #removeUnsupportedHevcLevels(
    hls: Hls,
    levels: CodecFilterCandidate[],
  ): boolean {
    if (!isGecko() || !Array.isArray(levels) || levels.length === 0) {
      return true;
    }

    const hevcIndices: number[] = [];
    let nonHevcCount = 0;

    for (let i = 0; i < levels.length; i++) {
      const codecSet = (levels[i].codecSet || '').toLowerCase();
      if (codecSet.includes('hvc1') || codecSet.includes('hev1')) {
        hevcIndices.push(i);
      } else {
        nonHevcCount++;
      }
    }

    if (hevcIndices.length === 0) return true;

    // HEVC-only stream on Firefox — no fallback available
    if (nonHevcCount === 0) return false;

    // Remove HEVC levels in reverse order to preserve indices
    for (let i = hevcIndices.length - 1; i >= 0; i--) {
      hls.removeLevel(hevcIndices[i]);
    }

    return true;
  }

  /**
   * Start level among `candidates`, whose originalIndex is their hls.js
   * index. Always answers from the candidates, so the codec choice applies
   * even when hls.js would otherwise run its own start selection: a player
   * without layout size or smaller than every rung starts on the lowest
   * candidate. With the player-size cap on, the answer never exceeds
   * hls.js's cap index, because hls.js begins on the cap rung itself (the
   * last codec of that size group) when the start level lies above it.
   */
  #pickStartLevelAmong(
    candidates: IndexedStartLevelInput[],
    hls: Hls,
  ): number {
    if (candidates.length === 0) return -1;
    const lowest = candidates[0].originalIndex ?? -1;
    const width = this.offsetWidth;
    const height = this.offsetHeight;
    if (width < 1 || height < 1) return lowest;

    const levelCap = Boolean(hls.config.capLevelToPlayerSize);
    let index = pickOriginalStartLevelForPlayerSize(
      candidates,
      width,
      height,
      levelCap,
      globalThis.devicePixelRatio ?? 1,
    );
    if (index < 0) index = lowest;
    if (levelCap) {
      const cap = getPlayerSizeCapIndex(hls.levels ?? [], width, height);
      if (cap >= 0 && index > cap) {
        const within = candidates.filter((candidate) =>
          (candidate.originalIndex ?? -1) <= cap
        );
        if (within.length > 0) {
          index = within[within.length - 1].originalIndex ?? index;
        }
      }
    }
    return index;
  }

  /** Start level for the selected family, or for the whole ladder. */
  #pickPreferredStartLevel(hls: Hls): number {
    const levels = hls.levels ?? [];
    const candidates = this._filteredLevels && this._filteredLevels.length > 0
      ? this._filteredLevels
      : levels.map((level, index) => ({ ...level, originalIndex: index }));
    return this.#pickStartLevelAmong(candidates, hls);
  }

  #createBaseHlsConfig(
    shouldPreload: boolean,
    useInternalFairPlay: boolean,
  ): Record<string, unknown> {
    return createBunnyHlsConfig({
      shouldPreload,
      edrmEnabled: this._edrmEnabled,
      useInternalFairPlay,
      levelCap: this._levelCap,
      isLiveManagedSource: this.#isLiveManagedSource(),
    });
  }

  /**
   * Get token query parameters from URL
   * @returns {string} Query string with token and expires params, or empty string
   */
  #getTokenQueryParams(): string {
    const url = new URL(globalThis.location.href);
    const token = url.searchParams.get('token') || '';
    const expires = parseInt(url.searchParams.get('expires') || '0', 10);

    if (token && expires) {
      return `token=${token}&expires=${expires}`;
    }
    return '';
  }

  /**
   * Get token query parameters with leading ? for URL construction
   * @returns {string} Query string with ? prefix, or empty string
   */
  #getTokenQueryString(): string {
    const params = this.#getTokenQueryParams();
    return params ? `?${params}` : '';
  }

  /**
   * Build FairPlay license URL
   * @returns {string} FairPlay license URL
   */
  #getFairPlayLicenseUrl(): string {
    const queryParams = this.#getTokenQueryParams();
    const separator = queryParams ? '&' : '';
    return `${this._baseUrl}/FairPlay/${this._libraryId}/license?videoId=${this._videoId}${separator}${queryParams}`;
  }

  /**
   * Build FairPlay certificate URL
   * @returns {string} FairPlay certificate URL
   */
  #getFairPlayCertificateUrl(): string {
    return `${this._baseUrl}/FairPlay/${this._libraryId}/certificate`;
  }

  /**
   * Build Widevine license URL
   * @returns {string} Widevine license URL
   */
  #getWidevineLicenseUrl(): string {
    const queryString = this.#getTokenQueryString();
    return `${this._baseUrl}/WidevineLicense/${this._libraryId}/${this._videoId}${queryString}`;
  }

  /**
   * Whether to skip hls.js and let the video element load the source itself.
   *
   * `native-hls` exists for multi-audio on iOS (STREAM-800). hls.js demuxes and appends
   * a single audio rendition to the MediaSource, so the video element only ever sees one
   * audio track - which is why iOS native fullscreen, driven by that element, offers no
   * audio menu. Playing the manifest natively gives Safari the alternate renditions, so
   * both its own fullscreen menu and ours (media-tracks mirrors the native list) work.
   *
   * Restricted to WebKit, which is the only engine that plays HLS itself. canPlayType is
   * not usable as the test - Chromium also answers "maybe" for
   * application/vnd.apple.mpegurl and then cannot play it, which leaves the video stuck
   * and the audio menu empty. eDRM keeps hls.js too; those paths configure their own key
   * handling.
   *
   * `auto` narrows it further, to browsers where native video fullscreen is the only
   * fullscreen. Desktop Safari and iPad can put our own chrome into fullscreen, so their
   * audio menu already works there and they keep hls.js - and with it the quality menu,
   * levelCap and CMCD. `safari` opts every WebKit in.
   */
  #shouldBypassHlsJs(): boolean {
    if (this.getAttribute('use-hlsjs') === 'false') return true;

    const mode = this.getAttribute('native-hls');
    if (mode !== 'auto' && mode !== 'safari') return false;
    if (this._edrmEnabled) return false;
    if (!isWebKitEngine()) return false;
    if (mode === 'auto' && this.#hasElementFullscreen()) return false;

    return Boolean(
      this.nativeEl?.canPlayType('application/vnd.apple.mpegurl'),
    );
  }

  /**
   * Whether the engine can put an element into fullscreen, which is what keeps our own
   * controls - and so the audio menu - on screen. iPhone has neither entry point, so
   * media-chrome falls back to the video element's native fullscreen.
   */
  #hasElementFullscreen(): boolean {
    return 'requestFullscreen' in Element.prototype ||
      'webkitRequestFullScreen' in Element.prototype;
  }

  override async load(): Promise<void> {
    BunnyStreamVideoElement.warmCodecCapabilities();
    // While casting the local hls.js instance stays alive, and with it the
    // quality menu of the current source.
    if (this.remote?.state !== 'connected') this.#setFilteredLevels(null);
    const nativeEl = this.nativeEl;
    this._selectedWidevineRobustness = null;
    this._restrictWidevineToSd = false;

    const preloadAttr = this.getAttribute('preload');
    if (nativeEl && preloadAttr) {
      nativeEl.preload = preloadAttr as '' | 'none' | 'metadata' | 'auto';
    }

    // Hand the source straight to the video element instead of hls.js. Two cases reach
    // here: a non-HLS source (e.g. original MP4), where hls.js would try to parse the
    // file as a manifest and fail in a retry loop; and `native-hls`, where we want
    // Safari to play the manifest itself.
    if (this.#shouldBypassHlsJs()) {
      // While casting, the castable mixin's load() must still run — it is
      // what sends the LoadRequest to the receiver. Skipping it here would
      // leave the cast session idle for original-source videos.
      if (this.remote?.state === 'connected') {
        await super.load();
        this.#syncCastAudioTrack();
        return;
      }
      if (nativeEl) {
        if (this.src) {
          nativeEl.src = this.src;
        } else {
          // The host source was removed: a live stream going offline
          // (#applyLiveState) or the iOS ad break. super.load() is skipped on this
          // path, so without unloading here the video keeps its old source, its
          // buffer and its playlist traffic.
          nativeEl.removeAttribute('src');
          nativeEl.load();
        }
      }
      if (this._startTime > 0) {
        this.#setupStartTimeSeeking();
      }
      return;
    }

    const shouldPreload = this.#shouldPreload();
    const useInternalFairPlay = this.#shouldUseInternalFairPlay();

    const basicHlsConfig = this.#createBaseHlsConfig(
      shouldPreload,
      useInternalFairPlay,
    );

    const cmcdConfig = this.#buildCmcdConfig();
    if (cmcdConfig) {
      basicHlsConfig.cmcd = cmcdConfig;
    }

    // JIT segments may 404 briefly while the routing script is still encoding
    // them on demand. Tolerate that with long, linear-backoff retries instead
    // of bubbling up a fatal network error. Must be set on the config before
    // super.load() so hls.js picks it up for the very first fragment request.
    if (this.isJit) {
      basicHlsConfig.fragLoadPolicy = {
        default: {
          maxTimeToFirstByteMs: 10000,
          maxLoadTimeMs: 120000,
          timeoutRetry: {
            maxNumRetry: 4,
            retryDelayMs: 0,
            maxRetryDelayMs: 0,
          },
          errorRetry: {
            maxNumRetry: 100,
            retryDelayMs: 1000,
            maxRetryDelayMs: 1000,
            backoff: 'linear',
          },
        },
      };
    }

    // Don't spread existing config - start fresh
    this.config = basicHlsConfig;

    // Only configure DRM if drmVersion is 1
    if (this._edrmEnabled) {
      if (useInternalFairPlay) {
        // Create StreamDrmEngine for native HLS FairPlay playback.
        const streamDrmEngine = new StreamDrmEngine(
          this.#getFairPlayLicenseUrl(),
          this.#getFairPlayCertificateUrl(),
          0,
        );
        this._streamDrmEngine = streamDrmEngine;
      } else {
        // Configure HLS.js DRM (modern Safari 17+, Chrome, Firefox, etc.)
        const isFairPlay = isFairPlayBrowser();

        // Only configure the DRM system for the current browser
        // Safari/iOS = FairPlay only, Chrome/Firefox/etc = Widevine only
        const widevineDrmSystem: Record<string, unknown> = {
          licenseUrl: this.#getWidevineLicenseUrl(),
        };

        const drmSystems = isFairPlay
          ? {
            'com.apple.fps': {
              licenseUrl: this.#getFairPlayLicenseUrl(),
              serverCertificateUrl: this.#getFairPlayCertificateUrl(),
            },
          }
          : {
            'com.widevine.alpha': widevineDrmSystem,
          };

        const hlsDrmConfig: Record<string, unknown> = {
          emeEnabled: true,
          drmSystemOptions: isFairPlay ? {} : await this.#getRobustness(),
          drmSystems,
        };
        if (!isFairPlay && this.#shouldResolveWidevinePolicyByCodec()) {
          hlsDrmConfig.requestMediaKeySystemAccessFunc = (
            keySystem: string,
            configurations: MediaKeySystemConfiguration[],
          ) =>
            this.#requestCodecAwareWidevineAccess(
              keySystem,
              configurations,
            );
        }

        // Merge DRM config with basic config
        this.config = {
          ...this.config,
          ...hlsDrmConfig,
        };
      }
    }

    // For native HLS FairPlay, HLS.js via MSE/MMS is bypassed so WebKit owns
    // both the playlist and encrypted media pipeline.
    if (useInternalFairPlay) {
      // Guard against double initialization - load() can be called multiple times
      if (this._internalDrmInitialized) {
        return;
      }
      this._internalDrmInitialized = true;

      try {
        // Get the native video element directly without initializing HLS.js
        const videoEl = this.nativeEl;
        if (!videoEl) {
          throw new Error(
            'Could not find video element for native HLS playback',
          );
        }

        // Wire up error handling from DRM engine
        // For native HLS path, dispatch on document since the element may not be in
        // the standard media-chrome shadow DOM tree
        const streamDrmEngine = this._streamDrmEngine;
        if (!streamDrmEngine) {
          throw new Error('FairPlay DRM engine is not initialized');
        }

        streamDrmEngine.onError = (code: string, message: string) => {
          const errorEvent = new CustomEvent('playererror', {
            bubbles: true,
            composed: true,
            detail: {
              type: 'drm',
              code,
              message,
              details: code,
            },
          });
          document.dispatchEvent(errorEvent);
          console.error(`[Player Error] drm: ${message}`, { details: code });
        };

        // Initialize FairPlay DRM before setting source
        await streamDrmEngine.initFairPlay(videoEl);

        // Set source directly for native HLS playback (required for FairPlay)
        videoEl.src = this.src;

        // Handle start time seeking
        if (this._startTime > 0) {
          this.#setupStartTimeSeeking();
        }
      } catch (error) {
        this._internalDrmInitialized = false; // Allow retry on error
        throw error;
      }

      return;
    }

    // Call parent load (uses HLS.js) for all other cases
    await super.load();

    // If this load happened as part of starting a cast session, reconcile a
    // pre-cast local audio selection with the receiver (no-op otherwise —
    // castable-video only bridges text tracks in its LoadRequest).
    this.#syncCastAudioTrack();

    // Handle start time seeking
    if (this._startTime > 0) {
      this.#setupStartTimeSeeking();
    }

    // Only proceed if we have HLS.js support and an api instance
    const hls = this.api;
    if (!hls || !this.src) {
      return;
    }

    // Bind HLS.js events
    hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
      this.#clearPlaylistRetry();
      this.#clearPlayerError();
      this._mediaErrorRecoveryAttempts = 0;
      this._manifestReloadAttemptedFor = null;
      this._forbiddenReattachPending = false;

      const levels = data.levels || [];
      // hls-video-element numbered its renditions from this same list.
      this.#manifestRenditionIds = new WeakMap<object, number>(
        levels.map((level, index) => [level, index]),
      );

      // On Firefox, remove HEVC levels from HLS.js to prevent broken seeking.
      // If the stream is HEVC-only, show an error since Firefox cannot handle it.
      if (!this.#removeUnsupportedHevcLevels(hls, levels)) {
        this.#dispatchPlayerError(
          'playback',
          'Codec Not Supported',
          'This video uses a codec (HEVC) that is not supported by your browser. Please try a different browser such as Chrome or Safari.',
          { codec: 'hevc', browser: 'firefox' },
        );
        hls.destroy();
        return;
      }

      if (!this.#pruneWidevinePlaybackLevels(hls, hls.levels || [])) {
        this.#dispatchPlayerError(
          'drm',
          'Encryption Error',
          "This video cannot be played on your device because it doesn't meet the content protection requirements.",
          {
            details: 'widevineNoPlayableLevel',
            maxSoftwareWidevineResolution: this._maxSoftwareWidevineResolution,
            selectedWidevineRobustness: this._selectedWidevineRobustness,
          },
        );
        hls.destroy();
        return;
      }

      this.#applyCodecFiltering(hls);

      // Jump directly to the highest quality of the selected codec family
      // that fits the player, rather than starting at the bottom and ABR-ing
      // up. This runs before hls.js starts loading, which is the only time
      // hls.js honours startLevel.
      const startLevel = this.#pickPreferredStartLevel(hls);
      if (startLevel >= 0) {
        hls.startLevel = startLevel;
      }
    });

    // hls.js removes levels on its own too (Media Capabilities reports a
    // rendition unsupported, key or codec errors). Keep the quality menu on
    // renditions that still exist; the rendition ids stay valid.
    hls.on(Hls.Events.LEVELS_UPDATED, () => {
      if (this.api === hls && this._filteredLevels) {
        this.#applyCodecFiltering(hls);
      }
    });

    hls.on(Hls.Events.ERROR, (_event, data) => {
      // Checked before the fatal filter: hls.js retries a refused live
      // playlist with backoff, stalling playback, and retrying the same
      // lapsed token cannot succeed while a newer signing is at hand.
      if (
        this.#isLiveManagedSource() && isPlaylistForbiddenError(data) &&
        this.#reattachNewerSigning()
      ) {
        return;
      }

      if (!data.fatal) return;

      // A live playlist that 404s has not been published yet - the stream just
      // went live, or a preview player is waiting for the broadcaster's first
      // bytes. hls.js has no recovery for this, so hand it to the live-status
      // poll, which reloads the source once the playlist appears. Covers the
      // live -> VOD transition too, where the VOD playlist is still being
      // written when the live one disappears.
      if (this.#isLiveManagedSource() && isPlaylistNotFoundError(data)) {
        hls.stopLoad();
        // The poll is now gating playback, not just change detection, so it
        // needs the failed URL, the fast cadence, and a rescheduled tick.
        this.#markPlaylistRetryPending(getHlsErrorUrl(data));
        return;
      }

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR: {
          const kind = classifyFatalNetworkError(data);
          if (kind === 'recoverable') {
            console.debug(
              'fatal network error encountered, trying to recover',
            );
            hls.startLoad();
            break;
          }
          // A failed manifest with no readable status (hls.js reports 0):
          // the edge answered without CORS headers, which is a refusal, or
          // nothing answered at all. hls.js has nothing left to do with this
          // instance either way, so ask which before choosing the message.
          if (
            kind === 'unavailable' && !getHlsErrorStatusCode(data) &&
            isManifestLoadError(data.details)
          ) {
            this.#resolveHiddenManifestFailure(hls, data);
            break;
          }
          this.#reportNetworkError(hls, kind, data);
          break;
        }
        case Hls.ErrorTypes.MEDIA_ERROR:
          this._mediaErrorRecoveryAttempts++;
          if (this._mediaErrorRecoveryAttempts <= 3) {
            console.debug(
              `fatal media error encountered, recovery attempt ${this._mediaErrorRecoveryAttempts}/3`,
            );
            hls.recoverMediaError();
          } else {
            console.debug(
              'fatal media error: max recovery attempts reached',
            );
            this.#dispatchPlayerError(
              'playback',
              'Source Not Supported',
              'An error occurred during playback. The media could not be loaded.',
              {
                details: data.details,
                reason: data.reason,
              },
            );
            hls.destroy();
          }
          break;
        default: {
          console.debug('unrecoverable error', data);
          const errorDetails = data.details || '';

          // Check if this is a DRM-related error
          if (isDrmError(errorDetails)) {
            // Check if running on HTTP (non-secure context)
            if (!this.#isSecureContext()) {
              this.#dispatchPlayerError(
                'drm',
                'DRM Requires HTTPS',
                'This video requires a secure connection (HTTPS) to play DRM-protected content.',
                {
                  details: data.details,
                  reason: data.reason,
                  isSecure: false,
                },
              );
            } else {
              // DRM error on HTTPS - use legacy-style message
              this.#dispatchPlayerError(
                'drm',
                'Encryption Error',
                this.#getDrmErrorMessage(errorDetails),
                {
                  details: data.details,
                  reason: data.reason,
                },
              );
            }
          } else {
            // Generic unrecoverable error
            this.#dispatchPlayerError(
              'playback',
              'Source Not Supported',
              'An error occurred during playback. The media could not be loaded.',
              {
                details: data.details,
                reason: data.reason,
              },
            );
          }

          hls.destroy();
          break;
        }
      }
    });
  }

  /**
   * Nothing to resume: a 4xx is never retried by hls.js and the same request
   * would just fail again, and a failed multivariant manifest leaves no level
   * for startLoad() to pick up. Tell the viewer why instead of leaving a blank
   * frame (STREAM-1120).
   */
  #reportNetworkError(
    hls: Hls,
    kind: Exclude<FatalNetworkErrorKind, 'recoverable'>,
    data: { details?: string; reason?: string },
  ): void {
    const { code, message } = this.#describeNetworkError(
      kind,
      data.details || '',
    );
    this.#dispatchPlayerError('network', code, message, {
      details: data.details,
      reason: data.reason,
      status: getHlsErrorStatusCode(data),
      url: getHlsErrorUrl(data),
    });
    hls.destroy();
  }

  /**
   * Ask the CDN directly what a hidden manifest failure was. A refusal gets
   * the security-settings message; a playlist that turns out to be served was
   * a failure on the way and is worth one reload, since hls.js cannot
   * re-request a manifest that never parsed; everything else is a network
   * error. The reload goes through #reloadSource so a viewer who already
   * pressed play is not left paused by load(). The answer is only applied
   * while this hls.js instance is still the current one.
   */
  #resolveHiddenManifestFailure(
    hls: Hls,
    data: { details?: string; reason?: string; url?: string },
  ): void {
    const url = getHlsErrorUrl(data) ?? this.getAttribute('src');
    void this.#probePlaylistDelivery(url).then((delivery) => {
      if (this.api !== hls) return;
      if (
        delivery === 'available' && url &&
        this._manifestReloadAttemptedFor !== url
      ) {
        this._manifestReloadAttemptedFor = url;
        this.#reloadSource();
        return;
      }
      this.#reportNetworkError(hls, this.#deliveryToErrorKind(delivery), data);
    });
  }

  #probePlaylistDelivery(url: string | null): Promise<PlaylistDelivery> {
    if (!url) return Promise.resolve('unknown');
    return classifyPlaylistDelivery(url);
  }

  #deliveryToErrorKind(
    delivery: PlaylistDelivery,
  ): Exclude<FatalNetworkErrorKind, 'recoverable'> {
    if (delivery === 'refused') return 'forbidden';
    if (delivery === 'missing') return 'not-found';
    return 'unavailable';
  }

  #attachNativeMediaListeners(): void {
    if (this.#nativeMediaListenersAttached || !this.nativeEl) return;
    this.nativeEl.addEventListener('error', this.#handleNativeMediaError);
    // hls.js clears on MANIFEST_PARSED; native playback has only the media
    // element's own events to say a load got somewhere.
    this.nativeEl.addEventListener('loadedmetadata', () => {
      if (this.api) return;
      this._manifestReloadAttemptedFor = null;
      this.#clearPlayerError();
    });
    this.#nativeMediaListenersAttached = true;
  }

  /**
   * Native HLS playback (the `native-hls` scope on WebKit) never goes through
   * hls.js, so a playlist the CDN refuses arrives as a bare media error with
   * no status and no URL. Ask the CDN the same question as on the hls.js path
   * so the viewer gets the same explanation, with the same one reload when the
   * playlist turns out to be served. An original-file source is left alone,
   * since a media error there is about the file, not delivery. A live source
   * only reports a refusal: its playlist comes and goes with the broadcast,
   * and the status poll owns that.
   */
  #handleNativeMediaError = (): void => {
    if (this.api) return;
    if (this.getAttribute('use-hlsjs') === 'false') return;
    const code = this.nativeEl?.error?.code;
    if (
      code !== MediaError.MEDIA_ERR_NETWORK &&
      code !== MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED
    ) return;
    const url = this.getAttribute('src');
    if (!url) return;
    const isLive = this.#isLiveManagedSource();
    void this.#probePlaylistDelivery(url).then((delivery) => {
      if (this.api || this.getAttribute('src') !== url) return;
      if (isLive && delivery !== 'refused') return;
      const details = { native: true, mediaErrorCode: code, url };
      if (delivery === 'available') {
        // Delivery works, so the failure was on the way or in the content.
        // One reload covers the former; after that, it is the content.
        if (this._manifestReloadAttemptedFor !== url) {
          this._manifestReloadAttemptedFor = url;
          this.#reloadSource();
          return;
        }
        this.#dispatchPlayerError(
          'playback',
          'Source Not Supported',
          'An error occurred during playback. The media could not be loaded.',
          details,
        );
        return;
      }
      const kind = this.#deliveryToErrorKind(delivery);
      const { code: errorCode, message } = this.#describeNetworkError(kind, '');
      this.#dispatchPlayerError('network', errorCode, message, details);
    });
  };

  /**
   * The message for a network error hls.js cannot recover from, by cause.
   *
   * A blocked request looks the same as a missing video to the player - both are
   * an empty frame - but they are different problems for whoever embedded it:
   * one is fixed in the library's security settings, the other by checking the
   * video exists. Say which. The refusal is worded for the viewer, who cannot
   * change those settings, and does not reveal which rule refused them; the
   * `Forbidden` code in the event detail is there for the embedder. Message
   * strings double as translation keys.
   */
  #describeNetworkError(
    kind: Exclude<FatalNetworkErrorKind, 'recoverable'>,
    errorDetails: string,
  ): { code: string; message: string } {
    // A rejected key request is a content protection failure to the viewer,
    // whatever status the key server used to say so.
    if (isDrmError(errorDetails)) {
      return {
        code: 'Encryption Error',
        message: 'There was an error with the content protection system.',
      };
    }

    switch (kind) {
      case 'forbidden':
        return {
          code: 'Forbidden',
          message: 'This video is not available.',
        };
      case 'not-found':
        return {
          code: 'Not Found',
          message: 'This video could not be found.',
        };
      case 'unavailable':
        return {
          code: 'Network Error',
          message: 'A network error caused the media download to fail.',
        };
    }
  }

  /**
   * Get a human-readable error message for DRM errors
   * Messages match the legacy player for consistency
   * @param {string} errorDetails - The HLS.js error details
   * @returns {string} Human-readable error message
   */
  #getDrmErrorMessage(errorDetails: string): string {
    switch (errorDetails) {
      case 'keySystemNoAccess':
        // Legacy message: "This video cannot be played on your device because it doesn't meet the content protection requirements."
        return "This video cannot be played on your device because it doesn't meet the content protection requirements.";
      case 'keySystemStatusOutputRestricted':
        return "This video cannot be played on your device because it doesn't meet the content protection requirements.";
      case 'keySystemNoKeys':
      case 'keySystemNoSession':
      case 'keySystemNoConfiguredLicense':
      case 'keySystemLicenseRequestFailed':
      case 'keySystemServerCertificateRequestFailed':
      case 'keySystemServerCertificateUpdateFailed':
      case 'keySystemSessionUpdateFailed':
      case 'keySystemStatusInternalError':
      case 'keyLoadError':
      case 'keyLoadTimeOut':
      case 'keySystemError':
      default:
        // Legacy message: "There was an error with the content protection system."
        return 'There was an error with the content protection system.';
    }
  }
}

BunnyStreamVideoElement.warmCodecCapabilities();
customElements.define('bunny-stream-video', BunnyStreamVideoElement);

export default BunnyStreamVideoElement;
